/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> = T | { [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never };
import gql from 'graphql-tag';
import * as VueApolloComposable from '@vue/apollo-composable';
import * as VueCompositionApi from 'vue';
export type ReactiveFunction<TParam> = () => TParam;
export enum Currency {
  Eur = 'eur',
  Sek = 'sek',
  Usd = 'usd'
}

export enum Discipline {
  DoubleDutch = 'DoubleDutch',
  SingleRope = 'SingleRope',
  Wheel = 'Wheel'
}

export type EventDefinitionInput = {
  /**
   * Athlete switches in a custom relay, so its steps can be attributed to
   * each athlete without an official timing track. The clock runs from zero
   * to totalDuration, so there is no End cue, and a Start cue is only
   * accepted at offset 0, where it names the opening stretch. Switches must
   * fall inside the event.
   */
  cues?: Array<TimingCueInput> | null | undefined;
  name: string;
  totalDuration: number;
};

export enum GroupInviteKind {
  /** An admin asked this person to join */
  Invited = 'Invited',
  /** This person redeemed the join code and is waiting to be let in */
  Requested = 'Requested'
}

export enum GroupInviteStatus {
  Accepted = 'Accepted',
  Declined = 'Declined',
  Pending = 'Pending'
}

export type GroupMemberInput = {
  /** Required for an athlete the group manages, rejected for a member with an account */
  name?: string | null | undefined;
  observer: boolean;
  role: GroupRole;
};

export enum GroupRole {
  /** May manage the group, the people in it and the scores shared with it */
  Admin = 'Admin',
  Member = 'Member'
}

export type ProductInput = {
  productId: string;
  quantity: number;
};

export type ProfileOptionsInput = {
  /** Stored as false while the profile isn't public */
  checklist: boolean;
  public: boolean;
  /** Stored as false while the profile isn't public */
  speed: boolean;
};

export type SpeedMarkInput = {
  schema: string;
  sequence: number;
  target?: number | null | undefined;
  timestamp: number;
  value?: number | null | undefined;
};

export type SpeedParticipantInput = {
  memberId: string | number;
  segmentIndex?: number | null | undefined;
};

/** Both fields state the whole record after the call, neither is a partial edit */
export type SpeedResultGroupInput = {
  /** The group the score belongs to, null for none, which also clears who competed */
  groupId?: string | number | null | undefined;
  /** Everyone who competed, replacing whoever was named before */
  participants: Array<SpeedParticipantInput>;
};

export type SpeedResultInput = {
  /** Required when no marks are provided, ignored (derived from the marks) otherwise */
  count?: number | null | undefined;
  eventDefinition?: EventDefinitionInput | null | undefined;
  eventDefinitionId?: string | number | null | undefined;
  /** Share the score with a group you are in, see SpeedResult.group */
  groupId?: string | number | null | undefined;
  /** Rulesets-compatible mark stream, see SpeedResult.marks */
  marks?: Array<SpeedMarkInput> | null | undefined;
  name?: string | null | undefined;
  /** Requires a groupId, see SpeedResult.participants */
  participants?: Array<SpeedParticipantInput> | null | undefined;
  /**
   * The steps of each leg of a relay, one per segment of the event and adding
   * up to `count`. Only for a score entered as a plain count, the legs of a
   * counted score come from its marks.
   */
  segmentCounts?: Array<number> | null | undefined;
  /**
   * Whether the event definition's audio was played while counting, in which
   * case the marks should contain a 'start' mark for the moment it started. A
   * known event's cues are stored with the result either way, see
   * SpeedResult.timingTrack.
   */
  withTimingTrack?: boolean | null | undefined;
};

export type SpeedResultUpdateInput = {
  /** Only allowed for results that were entered as a plain count */
  count?: number | null | undefined;
  eventDefinition?: EventDefinitionInput | null | undefined;
  eventDefinitionId?: string | number | null | undefined;
  /** Omit to keep the current name, pass null or an empty string to clear it */
  name?: string | null | undefined;
  /**
   * The steps of each leg, one per segment of the event and adding up to
   * `count`. One record with `count`: whenever a count is given this states
   * the legs in full, and leaving it out leaves the score with no legs. It is
   * never "omit to keep", so changing the event of a score that has legs means
   * giving the count and the legs again.
   */
  segmentCounts?: Array<number> | null | undefined;
};

export enum TagValueType {
  /** One of the tag's values, or several when the tag allows it */
  Enum = 'Enum',
  /** Present or not, holds no value */
  Flag = 'Flag',
  Number = 'Number'
}

export enum Theme {
  Dark = 'Dark',
  Light = 'Light'
}

export type TimingCueInput = {
  label?: string | null | undefined;
  offset: number;
  type: TimingCueType;
};

export enum TimingCueType {
  /** The stop signal */
  End = 'End',
  /** The go signal, the event's clock starts here */
  Start = 'Start',
  /** An athlete switch in a relay */
  Switch = 'Switch'
}

export type TrickSubmissionInput = {
  /** Must be true: the submitter grants a CC BY 4.0 licence to the submission */
  acceptLicence: boolean;
  alternativeNames?: Array<string> | null | undefined;
  /** The name to credit the submitter by, copied as it is given */
  attributionName: string;
  description?: string | null | undefined;
  discipline: Discipline;
  /** Language of the text fields, defaults to the user's language, then English */
  lang?: string | null | undefined;
  name: string;
};

export enum TrickSubmissionKind {
  /** A new trick, with its first video */
  Trick = 'Trick',
  /** Another video of a trick the Tricktionary already has */
  Video = 'Video'
}

export enum TrickSubmissionStatus {
  Accepted = 'Accepted',
  Pending = 'Pending',
  Rejected = 'Rejected'
}

export type TrickVideoSubmissionInput = {
  /** Must be true: the submitter grants a CC BY 4.0 licence to the video */
  acceptLicence: boolean;
  /** The name to credit the submitter by, copied as it is given */
  attributionName: string;
};

export type UserProfileInput = {
  /** Shown on the profile and next to contributions */
  name: string;
  /**
   * The handle the profile is reachable at, as /profile/<username>. 3 to 30
   * lowercase letters, digits, dots, dashes or underscores, starting and
   * ending with a letter or digit; uppercase is lowercased on save. Null or
   * omitted releases the current username, an empty string is rejected.
   */
  username?: string | null | undefined;
};

export enum VerificationLevel {
  Judge = 'JUDGE',
  Official = 'OFFICIAL'
}

export enum VideoHost {
  Mux = 'Mux',
  YouTube = 'YouTube'
}

export enum VideoType {
  /** One run of the trick at natural speed, the player makes the slow motion */
  FullSpeed = 'FullSpeed',
  /** An edited clip, the trick at full speed followed by the same run slowed down */
  SlowMo = 'SlowMo'
}

export enum VideoUploadStatus {
  Cancelled = 'Cancelled',
  Errored = 'Errored',
  Processing = 'Processing',
  Ready = 'Ready',
  Waiting = 'Waiting'
}

export type GroupBaseFragment = { id: string, name: string, members: Array<{ id: string, observer: boolean }>, myMembership: { id: string, role: GroupRole, observer: boolean } | null };

export type GroupInviteBaseFragment = { id: string, kind: GroupInviteKind, status: GroupInviteStatus, role: GroupRole, observer: boolean, expiresAt: number, createdAt: number, group: { id: string, name: string }, invitedBy: { id: string, name: string | null, username: string | null } | null };

export type GroupInviteAdminFragment = { id: string, kind: GroupInviteKind, status: GroupInviteStatus, role: GroupRole, observer: boolean, expiresAt: number, createdAt: number, user: { id: string, name: string | null, username: string | null }, member: { id: string, name: string } | null, invitedBy: { id: string, name: string | null, username: string | null } | null };

export type GroupMemberBaseFragment = { id: string, name: string, role: GroupRole, observer: boolean, createdAt: number, user: { id: string, name: string | null, username: string | null } | null };

export type ProfileHeaderFragment = { id: string, username: string | null, name: string | null, photo: string | null, profile: { public: boolean, checklist: boolean, speed: boolean }, checklistStats: { completed: number, levels: Array<{ level: string, completed: number, total: number }> } };

export type ProfileUserFragment = { id: string, username: string | null, name: string | null, photo: string | null, speedBests?: Array<{ total: { id: string, name: string | null, createdAt: number, count: number, counted: boolean, analysis: { duration: number, stepsPerSecond: number, maxStepsPerSecond: number, misses: number, stepsLost: number, stepsPerSecondSeries: Array<number>, segments: Array<{ index: number, label: string | null, start: number, end: number, count: number, stepsPerSecond: number }> } | null, eventDefinition: { id: string, name: string, totalDuration: number }, group: { id: string, name: string } | null, participants: Array<{ segmentIndex: number | null, member: { id: string, name: string, user: { id: string } | null } }> }, ownSegment: { count: number, stepsPerSecond: number | null, result: { id: string, name: string | null, createdAt: number, count: number, counted: boolean, analysis: { duration: number, stepsPerSecond: number, maxStepsPerSecond: number, misses: number, stepsLost: number, stepsPerSecondSeries: Array<number>, segments: Array<{ index: number, label: string | null, start: number, end: number, count: number, stepsPerSecond: number }> } | null, eventDefinition: { id: string, name: string, totalDuration: number }, group: { id: string, name: string } | null, participants: Array<{ segmentIndex: number | null, member: { id: string, name: string, user: { id: string } | null } }> }, segment: { index: number, label: string | null } | null } | null, eventDefinition: { id: string, name: string, totalDuration: number } }>, checklist?: Array<{ id: string, trick: { id: string, slug: string, discipline: Discipline, tags: Array<{ number: number | null, tag: { id: string, slug: string }, values: Array<{ id: string }> }>, en: { id: string, name: string } | null, localised?: { id: string, name: string } | null, ttLevels: Array<{ id: string, level: string }> } }>, profile: { public: boolean, checklist: boolean, speed: boolean }, checklistStats: { completed: number, levels: Array<{ level: string, completed: number, total: number }> } };

export type SpeedAnalysisFieldsFragment = { duration: number, stepsPerSecond: number, maxStepsPerSecond: number, misses: number, stepsLost: number, stepsPerSecondSeries: Array<number>, segments: Array<{ index: number, label: string | null, start: number, end: number, count: number, stepsPerSecond: number }> };

export type SpeedPersonalBestBaseFragment = { eventDefinition: { id: string, name: string, totalDuration: number }, total: { id: string, name: string | null, createdAt: number, count: number, counted: boolean, eventDefinition: { id: string, name: string, totalDuration: number }, group: { id: string, name: string } | null, participants: Array<{ segmentIndex: number | null, member: { id: string, name: string, user: { id: string } | null } }> }, ownSegment: { count: number, stepsPerSecond: number | null, segment: { index: number, label: string | null } | null, result: { id: string, name: string | null, createdAt: number, count: number, counted: boolean, eventDefinition: { id: string, name: string, totalDuration: number }, group: { id: string, name: string } | null, participants: Array<{ segmentIndex: number | null, member: { id: string, name: string, user: { id: string } | null } }> } } | null };

export type SpeedParticipantBaseFragment = { segmentIndex: number | null, member: { id: string, name: string, user: { id: string } | null } };

export type SpeedResultBaseFragment = { id: string, name: string | null, createdAt: number, count: number, counted: boolean, eventDefinition: { id: string, name: string, totalDuration: number }, group: { id: string, name: string } | null, participants: Array<{ segmentIndex: number | null, member: { id: string, name: string, user: { id: string } | null } }> };

export type SpeedResultSharingFragment = { id: string, creator: { id: string }, group: { id: string, myMembership: { id: string, role: GroupRole } | null } | null };

export type SpeedResultSegmentsFragment = { id: string, segmentCounts: Array<number> | null, segments: Array<{ index: number, label: string | null, start: number, end: number, count: number, stepsPerSecond: number }> };

export type SpeedSegmentFieldsFragment = { index: number, label: string | null, start: number, end: number, count: number, stepsPerSecond: number };

export type TimingTrackFieldsFragment = { audioUrl: string | null, cues: Array<{ type: TimingCueType, offset: number, label: string | null }> };

export type TrickBoxFragment = { id: string, slug: string, discipline: Discipline, tags: Array<{ number: number | null, tag: { id: string, slug: string }, values: Array<{ id: string }> }>, en: { id: string, name: string } | null, localised?: { id: string, name: string } | null, ttLevels: Array<{ id: string, level: string }> };

export type TrickSubmissionBaseFragment = { id: string, kind: TrickSubmissionKind, name: string | null, discipline: Discipline, status: TrickSubmissionStatus, reviewNote: string | null, createdAt: number, upload: { id: string, status: VideoUploadStatus, error: string | null }, trick: { id: string, slug: string, discipline: Discipline } | null };

export type CompleteTrickMutationVariables = Exact<{
  trickId: string | number;
  completed: boolean;
}>;


export type CompleteTrickMutation = { createTrickCompletion?: { id: string, createdAt: number, trick: { id: string }, recordedBy: { id: string, name: string | null } | null }, deleteTrickCompletion?: { id: string } | null };

export type CreateCheckoutSessionMutationVariables = Exact<{
  products: Array<ProductInput> | ProductInput;
  currency: Currency;
}>;


export type CreateCheckoutSessionMutation = { createCheckoutSession: { id: string, url: string } };

export type CreateSpeedResultMutationVariables = Exact<{
  data: SpeedResultInput;
}>;


export type CreateSpeedResultMutation = { createSpeedResult: { id: string, name: string | null, createdAt: number, count: number, counted: boolean, eventDefinition: { id: string, name: string, totalDuration: number }, group: { id: string, name: string } | null, participants: Array<{ segmentIndex: number | null, member: { id: string, name: string, user: { id: string } | null } }> } };

export type CreateTrickSubmissionMutationVariables = Exact<{
  data: TrickSubmissionInput;
}>;


export type CreateTrickSubmissionMutation = { createTrickSubmission: { id: string, kind: TrickSubmissionKind, name: string | null, discipline: Discipline, status: TrickSubmissionStatus, reviewNote: string | null, createdAt: number, upload: { id: string, url: string | null, status: VideoUploadStatus, error: string | null }, trick: { id: string, slug: string, discipline: Discipline } | null } };

export type CreateTrickVideoSubmissionMutationVariables = Exact<{
  trickId: string | number;
  data: TrickVideoSubmissionInput;
}>;


export type CreateTrickVideoSubmissionMutation = { createTrickVideoSubmission: { id: string, kind: TrickSubmissionKind, name: string | null, discipline: Discipline, status: TrickSubmissionStatus, reviewNote: string | null, createdAt: number, upload: { id: string, url: string | null, status: VideoUploadStatus, error: string | null }, trick: { id: string, slug: string, discipline: Discipline } | null } };

export type DeleteSpeedResultMutationVariables = Exact<{
  speedResultId: string | number;
}>;


export type DeleteSpeedResultMutation = { deleteSpeedResult: { id: string } };

export type ExcludeSpeedResultFromPersonalBestsMutationVariables = Exact<{
  speedResultId: string | number;
  excluded: boolean;
}>;


export type ExcludeSpeedResultFromPersonalBestsMutation = { excludeSpeedResultFromPersonalBests: { id: string, excludedFromPersonalBests: boolean } };

export type SetGroupMemberTrickCompletionMutationVariables = Exact<{
  memberId: string | number;
  trickId: string | number;
  completed: boolean;
}>;


export type SetGroupMemberTrickCompletionMutation = { setGroupMemberTrickCompletion: { id: string, createdAt: number, trick: { id: string }, recordedBy: { id: string, name: string | null } | null } | null };

export type CreateGroupMutationVariables = Exact<{
  name: string;
}>;


export type CreateGroupMutation = { createGroup: { id: string, name: string, members: Array<{ id: string, observer: boolean }>, myMembership: { id: string, role: GroupRole, observer: boolean } | null } };

export type RespondToGroupInviteMutationVariables = Exact<{
  inviteId: string | number;
  accept: boolean;
}>;


export type RespondToGroupInviteMutation = { respondToGroupInvite: { id: string, status: GroupInviteStatus, kind: GroupInviteKind, group: { id: string, name: string, members: Array<{ id: string, observer: boolean }>, myMembership: { id: string, role: GroupRole, observer: boolean } | null } } };

export type RequestToJoinGroupMutationVariables = Exact<{
  joinCode: string;
}>;


export type RequestToJoinGroupMutation = { requestToJoinGroup: { id: string, kind: GroupInviteKind, status: GroupInviteStatus, role: GroupRole, observer: boolean, expiresAt: number, createdAt: number, group: { id: string, name: string }, invitedBy: { id: string, name: string | null, username: string | null } | null } };

export type UpdateGroupMutationVariables = Exact<{
  groupId: string | number;
  name: string;
}>;


export type UpdateGroupMutation = { updateGroup: { id: string, name: string } };

export type AddGroupAthleteMutationVariables = Exact<{
  groupId: string | number;
  name: string;
}>;


export type AddGroupAthleteMutation = { addGroupAthlete: { id: string, name: string, role: GroupRole, observer: boolean, createdAt: number, user: { id: string, name: string | null, username: string | null } | null } };

export type UpdateGroupMemberMutationVariables = Exact<{
  memberId: string | number;
  data: GroupMemberInput;
}>;


export type UpdateGroupMemberMutation = { updateGroupMember: { id: string, name: string, role: GroupRole, observer: boolean, createdAt: number, user: { id: string, name: string | null, username: string | null } | null } };

export type RemoveGroupMemberMutationVariables = Exact<{
  memberId: string | number;
}>;


export type RemoveGroupMemberMutation = { removeGroupMember: { id: string, name: string, role: GroupRole, observer: boolean, createdAt: number, user: { id: string, name: string | null, username: string | null } | null } };

export type LeaveGroupMutationVariables = Exact<{
  groupId: string | number;
}>;


export type LeaveGroupMutation = { leaveGroup: { id: string } };

export type InviteToGroupMutationVariables = Exact<{
  groupId: string | number;
  usernameOrId: string | number;
  role: GroupRole;
  observer: boolean;
  memberId?: string | number | null | undefined;
}>;


export type InviteToGroupMutation = { inviteToGroup: { id: string, kind: GroupInviteKind, status: GroupInviteStatus, role: GroupRole, observer: boolean, expiresAt: number, createdAt: number, user: { id: string, name: string | null, username: string | null }, member: { id: string, name: string } | null, invitedBy: { id: string, name: string | null, username: string | null } | null } };

export type CancelGroupInviteMutationVariables = Exact<{
  inviteId: string | number;
}>;


export type CancelGroupInviteMutation = { cancelGroupInvite: { id: string, status: GroupInviteStatus } };

export type SetGroupJoinCodeMutationVariables = Exact<{
  groupId: string | number;
}>;


export type SetGroupJoinCodeMutation = { setGroupJoinCode: { id: string, joinCode: string | null } };

export type ClearGroupJoinCodeMutationVariables = Exact<{
  groupId: string | number;
}>;


export type ClearGroupJoinCodeMutation = { clearGroupJoinCode: { id: string, joinCode: string | null } };

export type RespondToGroupJoinRequestMutationVariables = Exact<{
  inviteId: string | number;
  accept: boolean;
  memberId?: string | number | null | undefined;
}>;


export type RespondToGroupJoinRequestMutation = { respondToGroupJoinRequest: { id: string, status: GroupInviteStatus, member: { id: string, name: string, role: GroupRole, observer: boolean, createdAt: number, user: { id: string, name: string | null, username: string | null } | null } | null } };

export type SetProfileOptionsMutationVariables = Exact<{
  data: ProfileOptionsInput;
}>;


export type SetProfileOptionsMutation = { setProfileOptions: { id: string, profile: { public: boolean, checklist: boolean, speed: boolean } } };

export type SetSpeedResultGroupMutationVariables = Exact<{
  speedResultId: string | number;
  data: SpeedResultGroupInput;
}>;


export type SetSpeedResultGroupMutation = { setSpeedResultGroup: { id: string, name: string | null, createdAt: number, count: number, counted: boolean, eventDefinition: { id: string, name: string, totalDuration: number }, group: { id: string, name: string, myMembership: { id: string, role: GroupRole } | null } | null, participants: Array<{ segmentIndex: number | null, member: { id: string, name: string, user: { id: string } | null } }>, creator: { id: string } } };

export type SetUserLangMutationVariables = Exact<{
  lang?: string | null | undefined;
}>;


export type SetUserLangMutation = { setUserLang: { id: string, lang: string | null } };

export type SetUserThemeMutationVariables = Exact<{
  theme?: Theme | null | undefined;
}>;


export type SetUserThemeMutation = { setUserTheme: { id: string, theme: Theme | null } };

export type UpdateSpeedResultMutationVariables = Exact<{
  speedResultId: string | number;
  data: SpeedResultUpdateInput;
}>;


export type UpdateSpeedResultMutation = { updateSpeedResult: { id: string, name: string | null, createdAt: number, count: number, counted: boolean, segmentCounts: Array<number> | null, eventDefinition: { id: string, name: string, totalDuration: number }, group: { id: string, name: string } | null, participants: Array<{ segmentIndex: number | null, member: { id: string, name: string, user: { id: string } | null } }>, segments: Array<{ index: number, label: string | null, start: number, end: number, count: number, stepsPerSecond: number }> } };

export type UpdateUserProfileMutationVariables = Exact<{
  data: UserProfileInput;
}>;


export type UpdateUserProfileMutation = { updateUserProfile: { id: string, username: string | null, name: string | null, photo: string | null } };

export type EventDefinitionsQueryVariables = Exact<{ [key: string]: never; }>;


export type EventDefinitionsQuery = { eventDefinitions: Array<{ id: string, name: string, totalDuration: number, eventDefinitionLookupCode: string | null, timingTrack: { audioUrl: string | null, cues: Array<{ type: TimingCueType, offset: number, label: string | null }> } | null }> };

export type GlobalStatsQueryVariables = Exact<{
  from?: number | null | undefined;
}>;


export type GlobalStatsQuery = { globalStats: { countedAt: number, tricks: number, completions: number, averageCompletions: number, acceptedSubmissions: number, levels: Array<{ level: string, tricks: number, averageCompletions: number }> } | null, globalStatsHistory: Array<{ countedAt: number, speedResults: number, speedSteps: number, levels: Array<{ level: string, completions: number }> }> };

export type GroupQueryVariables = Exact<{
  groupId: string | number;
}>;


export type GroupQuery = { group: { id: string, name: string, myMembership: { id: string, role: GroupRole, observer: boolean } | null } | null };

export type GroupMembersQueryVariables = Exact<{
  groupId: string | number;
}>;


export type GroupMembersQuery = { group: { id: string, myMembership: { id: string, role: GroupRole, observer: boolean } | null, members: Array<{ id: string, name: string, role: GroupRole, observer: boolean, createdAt: number, user: { id: string, name: string | null, username: string | null } | null }> } | null };

export type GroupAdminQueryVariables = Exact<{
  groupId: string | number;
}>;


export type GroupAdminQuery = { group: { id: string, joinCode: string | null, invites: Array<{ id: string, kind: GroupInviteKind, status: GroupInviteStatus, role: GroupRole, observer: boolean, expiresAt: number, createdAt: number, user: { id: string, name: string | null, username: string | null }, member: { id: string, name: string } | null, invitedBy: { id: string, name: string | null, username: string | null } | null }> } | null };

export type GroupChecklistsQueryVariables = Exact<{
  groupId: string | number;
  discipline?: Discipline | null | undefined;
  withLocalised: boolean;
  lang?: string | null | undefined;
}>;


export type GroupChecklistsQuery = { group: { id: string, myMembership: { id: string, role: GroupRole } | null, members: Array<{ id: string, name: string, observer: boolean, user: { id: string } | null, checklist: Array<{ id: string, trick: { id: string } }> }> } | null, tricks: Array<{ id: string, slug: string, discipline: Discipline, tags: Array<{ number: number | null, tag: { id: string, slug: string }, values: Array<{ id: string }> }>, en: { id: string, name: string } | null, localised?: { id: string, name: string } | null, ttLevels: Array<{ id: string, level: string }> }> };

export type GroupSpeedResultsQueryVariables = Exact<{
  groupId: string | number;
  limit?: number | null | undefined;
  startAfter?: number | null | undefined;
  eventDefinitionId?: string | number | null | undefined;
  constellation?: Array<string | number> | string | number | null | undefined;
}>;


export type GroupSpeedResultsQuery = { group: { id: string, speedResults: Array<{ id: string, name: string | null, createdAt: number, count: number, counted: boolean, eventDefinition: { id: string, name: string, totalDuration: number }, group: { id: string, name: string } | null, participants: Array<{ segmentIndex: number | null, member: { id: string, name: string, user: { id: string } | null } }> }> } | null };

export type GroupSpeedAnalysisQueryVariables = Exact<{
  groupId: string | number;
  limit?: number | null | undefined;
  startAfter?: number | null | undefined;
  eventDefinitionId?: string | number | null | undefined;
  constellation?: Array<string | number> | string | number | null | undefined;
}>;


export type GroupSpeedAnalysisQuery = { group: { id: string, speedResults: Array<{ id: string, name: string | null, createdAt: number, count: number, counted: boolean, analysis: { stepsPerSecond: number } | null, segments: Array<{ index: number, label: string | null, start: number, end: number, count: number, stepsPerSecond: number }>, eventDefinition: { id: string, name: string, totalDuration: number }, group: { id: string, name: string } | null, participants: Array<{ segmentIndex: number | null, member: { id: string, name: string, user: { id: string } | null } }> }> } | null };

export type GroupConstellationsQueryVariables = Exact<{
  groupId: string | number;
}>;


export type GroupConstellationsQuery = { group: { id: string, constellations: Array<{ key: string, resultCount: number, members: Array<{ id: string, name: string }> }> } | null };

export type GroupMemberBestsQueryVariables = Exact<{
  memberId: string | number;
}>;


export type GroupMemberBestsQuery = { groupMember: { id: string, speedBests: Array<{ eventDefinition: { id: string, name: string, totalDuration: number }, total: { id: string, name: string | null, createdAt: number, count: number, counted: boolean, eventDefinition: { id: string, name: string, totalDuration: number }, group: { id: string, name: string } | null, participants: Array<{ segmentIndex: number | null, member: { id: string, name: string, user: { id: string } | null } }> }, ownSegment: { count: number, stepsPerSecond: number | null, segment: { index: number, label: string | null } | null, result: { id: string, name: string | null, createdAt: number, count: number, counted: boolean, eventDefinition: { id: string, name: string, totalDuration: number }, group: { id: string, name: string } | null, participants: Array<{ segmentIndex: number | null, member: { id: string, name: string, user: { id: string } | null } }> } } | null }> } | null };

export type MyGroupsQueryVariables = Exact<{ [key: string]: never; }>;


export type MyGroupsQuery = { me: { id: string, groups: Array<{ id: string, name: string, members: Array<{ id: string, observer: boolean }>, myMembership: { id: string, role: GroupRole, observer: boolean } | null }> } | null };

export type GroupInvitesQueryVariables = Exact<{ [key: string]: never; }>;


export type GroupInvitesQuery = { me: { id: string, groupInvites: Array<{ id: string, kind: GroupInviteKind, status: GroupInviteStatus, role: GroupRole, observer: boolean, expiresAt: number, createdAt: number, group: { id: string, name: string }, invitedBy: { id: string, name: string | null, username: string | null } | null }> } | null };

export type GroupByJoinCodeQueryVariables = Exact<{
  joinCode: string;
}>;


export type GroupByJoinCodeQuery = { groupByJoinCode: { id: string, name: string } | null };

export type LanguagesQueryVariables = Exact<{ [key: string]: never; }>;


export type LanguagesQuery = { languages: Array<{ id: string, enabled: boolean }> };

export type MeQueryVariables = Exact<{
  withChecklist: boolean;
}>;


export type MeQuery = { me: { id: string, username: string | null, name: string | null, photo: string | null, email: string | null, lang: string | null, theme: Theme | null, profile: { public: boolean, checklist: boolean, speed: boolean }, checklist?: Array<{ id: string, createdAt: number, trick: { id: string }, recordedBy: { id: string, name: string | null } | null }> } | null };

export type NoticesQueryVariables = Exact<{
  lang: string;
}>;


export type NoticesQuery = { notices: Array<{ id: string, updatedAt: number, text: { lang: string, body: string, links: Array<{ label: string, url: string }> } }> };

export type ProductsQueryVariables = Exact<{ [key: string]: never; }>;


export type ProductsQuery = { products: Array<{ id: string, name: string, description: string | null, image: string | null, prices: Array<{ id: string, currency: Currency, unitAmount: number | null }> }>, shippingRates: Array<{ id: string, currency: Currency, unitAmount: number | null }> };

export type ProfileHeaderQueryVariables = Exact<{
  usernameOrId: string | number;
}>;


export type ProfileHeaderQuery = { user: { id: string, username: string | null, name: string | null, photo: string | null, profile: { public: boolean, checklist: boolean, speed: boolean }, checklistStats: { completed: number, levels: Array<{ level: string, completed: number, total: number }> } } | null };

export type ProfileDetailsQueryVariables = Exact<{
  usernameOrId: string | number;
  withChecklist: boolean;
  withSpeed: boolean;
  withLocalised: boolean;
  lang?: string | null | undefined;
}>;


export type ProfileDetailsQuery = { user: { id: string, username: string | null, name: string | null, photo: string | null, speedBests?: Array<{ total: { id: string, name: string | null, createdAt: number, count: number, counted: boolean, analysis: { duration: number, stepsPerSecond: number, maxStepsPerSecond: number, misses: number, stepsLost: number, stepsPerSecondSeries: Array<number>, segments: Array<{ index: number, label: string | null, start: number, end: number, count: number, stepsPerSecond: number }> } | null, eventDefinition: { id: string, name: string, totalDuration: number }, group: { id: string, name: string } | null, participants: Array<{ segmentIndex: number | null, member: { id: string, name: string, user: { id: string } | null } }> }, ownSegment: { count: number, stepsPerSecond: number | null, result: { id: string, name: string | null, createdAt: number, count: number, counted: boolean, analysis: { duration: number, stepsPerSecond: number, maxStepsPerSecond: number, misses: number, stepsLost: number, stepsPerSecondSeries: Array<number>, segments: Array<{ index: number, label: string | null, start: number, end: number, count: number, stepsPerSecond: number }> } | null, eventDefinition: { id: string, name: string, totalDuration: number }, group: { id: string, name: string } | null, participants: Array<{ segmentIndex: number | null, member: { id: string, name: string, user: { id: string } | null } }> }, segment: { index: number, label: string | null } | null } | null, eventDefinition: { id: string, name: string, totalDuration: number } }>, checklist?: Array<{ id: string, trick: { id: string, slug: string, discipline: Discipline, tags: Array<{ number: number | null, tag: { id: string, slug: string }, values: Array<{ id: string }> }>, en: { id: string, name: string } | null, localised?: { id: string, name: string } | null, ttLevels: Array<{ id: string, level: string }> } }>, profile: { public: boolean, checklist: boolean, speed: boolean }, checklistStats: { completed: number, levels: Array<{ level: string, completed: number, total: number }> } } | null };

export type MyProfileDetailsQueryVariables = Exact<{
  withChecklist: boolean;
  withSpeed: boolean;
  withLocalised: boolean;
  lang?: string | null | undefined;
}>;


export type MyProfileDetailsQuery = { me: { id: string, username: string | null, name: string | null, photo: string | null, speedBests?: Array<{ total: { id: string, name: string | null, createdAt: number, count: number, counted: boolean, analysis: { duration: number, stepsPerSecond: number, maxStepsPerSecond: number, misses: number, stepsLost: number, stepsPerSecondSeries: Array<number>, segments: Array<{ index: number, label: string | null, start: number, end: number, count: number, stepsPerSecond: number }> } | null, eventDefinition: { id: string, name: string, totalDuration: number }, group: { id: string, name: string } | null, participants: Array<{ segmentIndex: number | null, member: { id: string, name: string, user: { id: string } | null } }> }, ownSegment: { count: number, stepsPerSecond: number | null, result: { id: string, name: string | null, createdAt: number, count: number, counted: boolean, analysis: { duration: number, stepsPerSecond: number, maxStepsPerSecond: number, misses: number, stepsLost: number, stepsPerSecondSeries: Array<number>, segments: Array<{ index: number, label: string | null, start: number, end: number, count: number, stepsPerSecond: number }> } | null, eventDefinition: { id: string, name: string, totalDuration: number }, group: { id: string, name: string } | null, participants: Array<{ segmentIndex: number | null, member: { id: string, name: string, user: { id: string } | null } }> }, segment: { index: number, label: string | null } | null } | null, eventDefinition: { id: string, name: string, totalDuration: number } }>, checklist?: Array<{ id: string, trick: { id: string, slug: string, discipline: Discipline, tags: Array<{ number: number | null, tag: { id: string, slug: string }, values: Array<{ id: string }> }>, en: { id: string, name: string } | null, localised?: { id: string, name: string } | null, ttLevels: Array<{ id: string, level: string }> } }>, profile: { public: boolean, checklist: boolean, speed: boolean }, checklistStats: { completed: number, levels: Array<{ level: string, completed: number, total: number }> } } | null };

export type RulesetsQueryVariables = Exact<{
  lang?: string | null | undefined;
}>;


export type RulesetsQuery = { rulesets: Array<{ id: string, name: string, isPrimary: boolean }> };

export type SpeedResultQueryVariables = Exact<{
  speedResultId: string | number;
}>;


export type SpeedResultQuery = { me: { id: string, speedResult: { excludedFromPersonalBests: boolean, id: string, name: string | null, createdAt: number, count: number, counted: boolean, segmentCounts: Array<number> | null, eventDefinition: { id: string, name: string, totalDuration: number, timingTrack: { cues: Array<{ type: TimingCueType, offset: number, label: string | null }> } | null }, analysis: { duration: number, stepsPerSecond: number, maxStepsPerSecond: number, misses: number, stepsLost: number, stepsPerSecondSeries: Array<number>, segments: Array<{ index: number, label: string | null, start: number, end: number, count: number, stepsPerSecond: number }> } | null, group: { id: string, name: string, myMembership: { id: string, role: GroupRole } | null } | null, participants: Array<{ segmentIndex: number | null, member: { id: string, name: string, user: { id: string } | null } }>, creator: { id: string }, segments: Array<{ index: number, label: string | null, start: number, end: number, count: number, stepsPerSecond: number }> } | null } | null };

export type SpeedResultsQueryVariables = Exact<{
  limit?: number | null | undefined;
  startAfter?: number | null | undefined;
  eventDefinitionId?: string | number | null | undefined;
  groupId?: string | number | null | undefined;
}>;


export type SpeedResultsQuery = { me: { id: string, speedResults: Array<{ id: string, name: string | null, createdAt: number, count: number, counted: boolean, eventDefinition: { id: string, name: string, totalDuration: number }, group: { id: string, name: string } | null, participants: Array<{ segmentIndex: number | null, member: { id: string, name: string, user: { id: string } | null } }> }> } | null };

export type TagsQueryVariables = Exact<{
  lang?: string | null | undefined;
}>;


export type TagsQuery = { tags: Array<{ id: string, slug: string, disciplines: Array<Discipline>, name: string, valueType: TagValueType, system: boolean, values: Array<{ id: string, name: string }> }> };

export type TrickBySlugQueryVariables = Exact<{
  discipline: Discipline;
  slug: string;
  withLocalised: boolean;
  lang?: string | null | undefined;
}>;


export type TrickBySlugQuery = { trick: { id: string, slug: string, discipline: Discipline, en: { id: string, name: string, alternativeNames: Array<string> | null, description: string | null } | null, localised?: { id: string, name: string, alternativeNames: Array<string> | null, description: string | null } | null, videos: Array<{ videoId: string, type: VideoType, host: VideoHost }>, contributors: Array<{ name: string }>, levels: Array<{ id: string, rulesId: string, level: string, verificationLevel: VerificationLevel | null }>, prerequisites: Array<{ id: string, slug: string, discipline: Discipline, tags: Array<{ number: number | null, tag: { id: string, slug: string }, values: Array<{ id: string }> }>, en: { id: string, name: string } | null, localised?: { id: string, name: string } | null, ttLevels: Array<{ id: string, level: string }> }>, prerequisiteFor: Array<{ id: string, slug: string, discipline: Discipline, tags: Array<{ number: number | null, tag: { id: string, slug: string }, values: Array<{ id: string }> }>, en: { id: string, name: string } | null, localised?: { id: string, name: string } | null, ttLevels: Array<{ id: string, level: string }> }>, tags: Array<{ number: number | null, tag: { id: string, slug: string }, values: Array<{ id: string }> }>, ttLevels: Array<{ id: string, level: string }> } | null };

export type MyTrickSubmissionsQueryVariables = Exact<{
  withLocalised: boolean;
  lang?: string | null | undefined;
}>;


export type MyTrickSubmissionsQuery = { me: { id: string, trickSubmissions: Array<{ id: string, kind: TrickSubmissionKind, name: string | null, discipline: Discipline, status: TrickSubmissionStatus, reviewNote: string | null, createdAt: number, trick: { id: string, slug: string, discipline: Discipline, en: { id: string, name: string } | null, localised?: { id: string, name: string } | null } | null, upload: { id: string, status: VideoUploadStatus, error: string | null } }> } | null };

export type TricksQueryVariables = Exact<{
  discipline?: Discipline | null | undefined;
  searchQuery?: string | null | undefined;
  withLocalised: boolean;
  lang?: string | null | undefined;
}>;


export type TricksQuery = { tricks: Array<{ id: string, slug: string, discipline: Discipline, tags: Array<{ number: number | null, tag: { id: string, slug: string }, values: Array<{ id: string }> }>, en: { id: string, name: string } | null, localised?: { id: string, name: string } | null, ttLevels: Array<{ id: string, level: string }> }> };

export type UiMessagesQueryVariables = Exact<{
  lang: string;
}>;


export type UiMessagesQuery = { uiMessages: Record<string, unknown> };

export const GroupBaseFragmentDoc = gql`
    fragment GroupBase on Group {
  id
  name
  members {
    id
    observer
  }
  myMembership {
    id
    role
    observer
  }
}
    `;
export const GroupInviteBaseFragmentDoc = gql`
    fragment GroupInviteBase on GroupInvite {
  id
  kind
  status
  role
  observer
  expiresAt
  createdAt
  group {
    id
    name
  }
  invitedBy {
    id
    name
    username
  }
}
    `;
export const GroupInviteAdminFragmentDoc = gql`
    fragment GroupInviteAdmin on GroupInvite {
  id
  kind
  status
  role
  observer
  expiresAt
  createdAt
  user {
    id
    name
    username
  }
  member {
    id
    name
  }
  invitedBy {
    id
    name
    username
  }
}
    `;
export const GroupMemberBaseFragmentDoc = gql`
    fragment GroupMemberBase on GroupMember {
  id
  name
  role
  observer
  createdAt
  user {
    id
    name
    username
  }
}
    `;
export const ProfileHeaderFragmentDoc = gql`
    fragment ProfileHeader on User {
  id
  username
  name
  photo
  profile {
    public
    checklist
    speed
  }
  checklistStats {
    completed
    levels {
      level
      completed
      total
    }
  }
}
    `;
export const SpeedParticipantBaseFragmentDoc = gql`
    fragment SpeedParticipantBase on SpeedParticipant {
  segmentIndex
  member {
    id
    name
    user {
      id
    }
  }
}
    `;
export const SpeedResultBaseFragmentDoc = gql`
    fragment SpeedResultBase on SpeedResult {
  id
  name
  createdAt
  count
  counted
  eventDefinition {
    id
    name
    totalDuration
  }
  group {
    id
    name
  }
  participants {
    ...SpeedParticipantBase
  }
}
    ${SpeedParticipantBaseFragmentDoc}`;
export const SpeedPersonalBestBaseFragmentDoc = gql`
    fragment SpeedPersonalBestBase on SpeedPersonalBest {
  eventDefinition {
    id
    name
    totalDuration
  }
  total {
    ...SpeedResultBase
  }
  ownSegment {
    count
    stepsPerSecond
    segment {
      index
      label
    }
    result {
      ...SpeedResultBase
    }
  }
}
    ${SpeedResultBaseFragmentDoc}`;
export const SpeedSegmentFieldsFragmentDoc = gql`
    fragment SpeedSegmentFields on SpeedSegment {
  index
  label
  start
  end
  count
  stepsPerSecond
}
    `;
export const SpeedAnalysisFieldsFragmentDoc = gql`
    fragment SpeedAnalysisFields on SpeedAnalysis {
  duration
  stepsPerSecond
  maxStepsPerSecond
  misses
  stepsLost
  stepsPerSecondSeries
  segments {
    ...SpeedSegmentFields
  }
}
    ${SpeedSegmentFieldsFragmentDoc}`;
export const TrickBoxFragmentDoc = gql`
    fragment TrickBox on Trick {
  id
  slug
  discipline
  tags {
    tag {
      id
      slug
    }
    number
    values {
      id
    }
  }
  en: localisation {
    id
    name
  }
  localised: localisation(lang: $lang) @include(if: $withLocalised) {
    id
    name
  }
  ttLevels: levels(rulesId: "tricktionary") {
    id
    level
  }
}
    `;
export const ProfileUserFragmentDoc = gql`
    fragment ProfileUser on User {
  ...ProfileHeader
  speedBests @include(if: $withSpeed) {
    ...SpeedPersonalBestBase
    total {
      analysis {
        ...SpeedAnalysisFields
      }
    }
    ownSegment {
      result {
        analysis {
          ...SpeedAnalysisFields
        }
      }
    }
  }
  checklist @include(if: $withChecklist) {
    id
    trick {
      ...TrickBox
    }
  }
}
    ${ProfileHeaderFragmentDoc}
${SpeedPersonalBestBaseFragmentDoc}
${SpeedAnalysisFieldsFragmentDoc}
${TrickBoxFragmentDoc}`;
export const SpeedResultSharingFragmentDoc = gql`
    fragment SpeedResultSharing on SpeedResult {
  id
  creator {
    id
  }
  group {
    id
    myMembership {
      id
      role
    }
  }
}
    `;
export const SpeedResultSegmentsFragmentDoc = gql`
    fragment SpeedResultSegments on SpeedResult {
  id
  segmentCounts
  segments {
    ...SpeedSegmentFields
  }
}
    ${SpeedSegmentFieldsFragmentDoc}`;
export const TimingTrackFieldsFragmentDoc = gql`
    fragment TimingTrackFields on TimingTrack {
  audioUrl
  cues {
    type
    offset
    label
  }
}
    `;
export const TrickSubmissionBaseFragmentDoc = gql`
    fragment TrickSubmissionBase on TrickSubmission {
  id
  kind
  name
  discipline
  status
  reviewNote
  createdAt
  upload {
    id
    status
    error
  }
  trick {
    id
    slug
    discipline
  }
}
    `;
export const CompleteTrickDocument = gql`
    mutation CompleteTrick($trickId: ID!, $completed: Boolean!) {
  createTrickCompletion(trickId: $trickId) @include(if: $completed) {
    id
    createdAt
    trick {
      id
    }
    recordedBy {
      id
      name
    }
  }
  deleteTrickCompletion(trickId: $trickId) @skip(if: $completed) {
    id
  }
}
    `;

/**
 * __useCompleteTrickMutation__
 *
 * To run a mutation, you first call `useCompleteTrickMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useCompleteTrickMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useCompleteTrickMutation({
 *   variables: {
 *     trickId: // value for 'trickId'
 *     completed: // value for 'completed'
 *   },
 * });
 */
export function useCompleteTrickMutation(options: VueApolloComposable.UseMutationOptions<CompleteTrickMutation, CompleteTrickMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<CompleteTrickMutation, CompleteTrickMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<CompleteTrickMutation, CompleteTrickMutationVariables>(CompleteTrickDocument, options);
}
export type CompleteTrickMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<CompleteTrickMutation, CompleteTrickMutationVariables>;
export const CreateCheckoutSessionDocument = gql`
    mutation CreateCheckoutSession($products: [ProductInput!]!, $currency: Currency!) {
  createCheckoutSession(products: $products, currency: $currency) {
    id
    url
  }
}
    `;

/**
 * __useCreateCheckoutSessionMutation__
 *
 * To run a mutation, you first call `useCreateCheckoutSessionMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useCreateCheckoutSessionMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useCreateCheckoutSessionMutation({
 *   variables: {
 *     products: // value for 'products'
 *     currency: // value for 'currency'
 *   },
 * });
 */
export function useCreateCheckoutSessionMutation(options: VueApolloComposable.UseMutationOptions<CreateCheckoutSessionMutation, CreateCheckoutSessionMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<CreateCheckoutSessionMutation, CreateCheckoutSessionMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<CreateCheckoutSessionMutation, CreateCheckoutSessionMutationVariables>(CreateCheckoutSessionDocument, options);
}
export type CreateCheckoutSessionMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<CreateCheckoutSessionMutation, CreateCheckoutSessionMutationVariables>;
export const CreateSpeedResultDocument = gql`
    mutation CreateSpeedResult($data: SpeedResultInput!) {
  createSpeedResult(data: $data) {
    ...SpeedResultBase
  }
}
    ${SpeedResultBaseFragmentDoc}`;

/**
 * __useCreateSpeedResultMutation__
 *
 * To run a mutation, you first call `useCreateSpeedResultMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useCreateSpeedResultMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useCreateSpeedResultMutation({
 *   variables: {
 *     data: // value for 'data'
 *   },
 * });
 */
export function useCreateSpeedResultMutation(options: VueApolloComposable.UseMutationOptions<CreateSpeedResultMutation, CreateSpeedResultMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<CreateSpeedResultMutation, CreateSpeedResultMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<CreateSpeedResultMutation, CreateSpeedResultMutationVariables>(CreateSpeedResultDocument, options);
}
export type CreateSpeedResultMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<CreateSpeedResultMutation, CreateSpeedResultMutationVariables>;
export const CreateTrickSubmissionDocument = gql`
    mutation CreateTrickSubmission($data: TrickSubmissionInput!) {
  createTrickSubmission(data: $data) {
    ...TrickSubmissionBase
    upload {
      id
      url
    }
  }
}
    ${TrickSubmissionBaseFragmentDoc}`;

/**
 * __useCreateTrickSubmissionMutation__
 *
 * To run a mutation, you first call `useCreateTrickSubmissionMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useCreateTrickSubmissionMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useCreateTrickSubmissionMutation({
 *   variables: {
 *     data: // value for 'data'
 *   },
 * });
 */
export function useCreateTrickSubmissionMutation(options: VueApolloComposable.UseMutationOptions<CreateTrickSubmissionMutation, CreateTrickSubmissionMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<CreateTrickSubmissionMutation, CreateTrickSubmissionMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<CreateTrickSubmissionMutation, CreateTrickSubmissionMutationVariables>(CreateTrickSubmissionDocument, options);
}
export type CreateTrickSubmissionMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<CreateTrickSubmissionMutation, CreateTrickSubmissionMutationVariables>;
export const CreateTrickVideoSubmissionDocument = gql`
    mutation CreateTrickVideoSubmission($trickId: ID!, $data: TrickVideoSubmissionInput!) {
  createTrickVideoSubmission(trickId: $trickId, data: $data) {
    ...TrickSubmissionBase
    upload {
      id
      url
    }
  }
}
    ${TrickSubmissionBaseFragmentDoc}`;

/**
 * __useCreateTrickVideoSubmissionMutation__
 *
 * To run a mutation, you first call `useCreateTrickVideoSubmissionMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useCreateTrickVideoSubmissionMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useCreateTrickVideoSubmissionMutation({
 *   variables: {
 *     trickId: // value for 'trickId'
 *     data: // value for 'data'
 *   },
 * });
 */
export function useCreateTrickVideoSubmissionMutation(options: VueApolloComposable.UseMutationOptions<CreateTrickVideoSubmissionMutation, CreateTrickVideoSubmissionMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<CreateTrickVideoSubmissionMutation, CreateTrickVideoSubmissionMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<CreateTrickVideoSubmissionMutation, CreateTrickVideoSubmissionMutationVariables>(CreateTrickVideoSubmissionDocument, options);
}
export type CreateTrickVideoSubmissionMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<CreateTrickVideoSubmissionMutation, CreateTrickVideoSubmissionMutationVariables>;
export const DeleteSpeedResultDocument = gql`
    mutation DeleteSpeedResult($speedResultId: ID!) {
  deleteSpeedResult(speedResultId: $speedResultId) {
    id
  }
}
    `;

/**
 * __useDeleteSpeedResultMutation__
 *
 * To run a mutation, you first call `useDeleteSpeedResultMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useDeleteSpeedResultMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useDeleteSpeedResultMutation({
 *   variables: {
 *     speedResultId: // value for 'speedResultId'
 *   },
 * });
 */
export function useDeleteSpeedResultMutation(options: VueApolloComposable.UseMutationOptions<DeleteSpeedResultMutation, DeleteSpeedResultMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<DeleteSpeedResultMutation, DeleteSpeedResultMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<DeleteSpeedResultMutation, DeleteSpeedResultMutationVariables>(DeleteSpeedResultDocument, options);
}
export type DeleteSpeedResultMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<DeleteSpeedResultMutation, DeleteSpeedResultMutationVariables>;
export const ExcludeSpeedResultFromPersonalBestsDocument = gql`
    mutation ExcludeSpeedResultFromPersonalBests($speedResultId: ID!, $excluded: Boolean!) {
  excludeSpeedResultFromPersonalBests(
    speedResultId: $speedResultId
    excluded: $excluded
  ) {
    id
    excludedFromPersonalBests
  }
}
    `;

/**
 * __useExcludeSpeedResultFromPersonalBestsMutation__
 *
 * To run a mutation, you first call `useExcludeSpeedResultFromPersonalBestsMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useExcludeSpeedResultFromPersonalBestsMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useExcludeSpeedResultFromPersonalBestsMutation({
 *   variables: {
 *     speedResultId: // value for 'speedResultId'
 *     excluded: // value for 'excluded'
 *   },
 * });
 */
export function useExcludeSpeedResultFromPersonalBestsMutation(options: VueApolloComposable.UseMutationOptions<ExcludeSpeedResultFromPersonalBestsMutation, ExcludeSpeedResultFromPersonalBestsMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<ExcludeSpeedResultFromPersonalBestsMutation, ExcludeSpeedResultFromPersonalBestsMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<ExcludeSpeedResultFromPersonalBestsMutation, ExcludeSpeedResultFromPersonalBestsMutationVariables>(ExcludeSpeedResultFromPersonalBestsDocument, options);
}
export type ExcludeSpeedResultFromPersonalBestsMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<ExcludeSpeedResultFromPersonalBestsMutation, ExcludeSpeedResultFromPersonalBestsMutationVariables>;
export const SetGroupMemberTrickCompletionDocument = gql`
    mutation SetGroupMemberTrickCompletion($memberId: ID!, $trickId: ID!, $completed: Boolean!) {
  setGroupMemberTrickCompletion(
    memberId: $memberId
    trickId: $trickId
    completed: $completed
  ) {
    id
    createdAt
    trick {
      id
    }
    recordedBy {
      id
      name
    }
  }
}
    `;

/**
 * __useSetGroupMemberTrickCompletionMutation__
 *
 * To run a mutation, you first call `useSetGroupMemberTrickCompletionMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useSetGroupMemberTrickCompletionMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useSetGroupMemberTrickCompletionMutation({
 *   variables: {
 *     memberId: // value for 'memberId'
 *     trickId: // value for 'trickId'
 *     completed: // value for 'completed'
 *   },
 * });
 */
export function useSetGroupMemberTrickCompletionMutation(options: VueApolloComposable.UseMutationOptions<SetGroupMemberTrickCompletionMutation, SetGroupMemberTrickCompletionMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<SetGroupMemberTrickCompletionMutation, SetGroupMemberTrickCompletionMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<SetGroupMemberTrickCompletionMutation, SetGroupMemberTrickCompletionMutationVariables>(SetGroupMemberTrickCompletionDocument, options);
}
export type SetGroupMemberTrickCompletionMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<SetGroupMemberTrickCompletionMutation, SetGroupMemberTrickCompletionMutationVariables>;
export const CreateGroupDocument = gql`
    mutation CreateGroup($name: String!) {
  createGroup(name: $name) {
    ...GroupBase
  }
}
    ${GroupBaseFragmentDoc}`;

/**
 * __useCreateGroupMutation__
 *
 * To run a mutation, you first call `useCreateGroupMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useCreateGroupMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useCreateGroupMutation({
 *   variables: {
 *     name: // value for 'name'
 *   },
 * });
 */
export function useCreateGroupMutation(options: VueApolloComposable.UseMutationOptions<CreateGroupMutation, CreateGroupMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<CreateGroupMutation, CreateGroupMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<CreateGroupMutation, CreateGroupMutationVariables>(CreateGroupDocument, options);
}
export type CreateGroupMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<CreateGroupMutation, CreateGroupMutationVariables>;
export const RespondToGroupInviteDocument = gql`
    mutation RespondToGroupInvite($inviteId: ID!, $accept: Boolean!) {
  respondToGroupInvite(inviteId: $inviteId, accept: $accept) {
    id
    status
    kind
    group {
      ...GroupBase
    }
  }
}
    ${GroupBaseFragmentDoc}`;

/**
 * __useRespondToGroupInviteMutation__
 *
 * To run a mutation, you first call `useRespondToGroupInviteMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useRespondToGroupInviteMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useRespondToGroupInviteMutation({
 *   variables: {
 *     inviteId: // value for 'inviteId'
 *     accept: // value for 'accept'
 *   },
 * });
 */
export function useRespondToGroupInviteMutation(options: VueApolloComposable.UseMutationOptions<RespondToGroupInviteMutation, RespondToGroupInviteMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<RespondToGroupInviteMutation, RespondToGroupInviteMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<RespondToGroupInviteMutation, RespondToGroupInviteMutationVariables>(RespondToGroupInviteDocument, options);
}
export type RespondToGroupInviteMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<RespondToGroupInviteMutation, RespondToGroupInviteMutationVariables>;
export const RequestToJoinGroupDocument = gql`
    mutation RequestToJoinGroup($joinCode: String!) {
  requestToJoinGroup(joinCode: $joinCode) {
    ...GroupInviteBase
  }
}
    ${GroupInviteBaseFragmentDoc}`;

/**
 * __useRequestToJoinGroupMutation__
 *
 * To run a mutation, you first call `useRequestToJoinGroupMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useRequestToJoinGroupMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useRequestToJoinGroupMutation({
 *   variables: {
 *     joinCode: // value for 'joinCode'
 *   },
 * });
 */
export function useRequestToJoinGroupMutation(options: VueApolloComposable.UseMutationOptions<RequestToJoinGroupMutation, RequestToJoinGroupMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<RequestToJoinGroupMutation, RequestToJoinGroupMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<RequestToJoinGroupMutation, RequestToJoinGroupMutationVariables>(RequestToJoinGroupDocument, options);
}
export type RequestToJoinGroupMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<RequestToJoinGroupMutation, RequestToJoinGroupMutationVariables>;
export const UpdateGroupDocument = gql`
    mutation UpdateGroup($groupId: ID!, $name: String!) {
  updateGroup(groupId: $groupId, name: $name) {
    id
    name
  }
}
    `;

/**
 * __useUpdateGroupMutation__
 *
 * To run a mutation, you first call `useUpdateGroupMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useUpdateGroupMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useUpdateGroupMutation({
 *   variables: {
 *     groupId: // value for 'groupId'
 *     name: // value for 'name'
 *   },
 * });
 */
export function useUpdateGroupMutation(options: VueApolloComposable.UseMutationOptions<UpdateGroupMutation, UpdateGroupMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<UpdateGroupMutation, UpdateGroupMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<UpdateGroupMutation, UpdateGroupMutationVariables>(UpdateGroupDocument, options);
}
export type UpdateGroupMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<UpdateGroupMutation, UpdateGroupMutationVariables>;
export const AddGroupAthleteDocument = gql`
    mutation AddGroupAthlete($groupId: ID!, $name: String!) {
  addGroupAthlete(groupId: $groupId, name: $name) {
    ...GroupMemberBase
  }
}
    ${GroupMemberBaseFragmentDoc}`;

/**
 * __useAddGroupAthleteMutation__
 *
 * To run a mutation, you first call `useAddGroupAthleteMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useAddGroupAthleteMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useAddGroupAthleteMutation({
 *   variables: {
 *     groupId: // value for 'groupId'
 *     name: // value for 'name'
 *   },
 * });
 */
export function useAddGroupAthleteMutation(options: VueApolloComposable.UseMutationOptions<AddGroupAthleteMutation, AddGroupAthleteMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<AddGroupAthleteMutation, AddGroupAthleteMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<AddGroupAthleteMutation, AddGroupAthleteMutationVariables>(AddGroupAthleteDocument, options);
}
export type AddGroupAthleteMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<AddGroupAthleteMutation, AddGroupAthleteMutationVariables>;
export const UpdateGroupMemberDocument = gql`
    mutation UpdateGroupMember($memberId: ID!, $data: GroupMemberInput!) {
  updateGroupMember(memberId: $memberId, data: $data) {
    ...GroupMemberBase
  }
}
    ${GroupMemberBaseFragmentDoc}`;

/**
 * __useUpdateGroupMemberMutation__
 *
 * To run a mutation, you first call `useUpdateGroupMemberMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useUpdateGroupMemberMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useUpdateGroupMemberMutation({
 *   variables: {
 *     memberId: // value for 'memberId'
 *     data: // value for 'data'
 *   },
 * });
 */
export function useUpdateGroupMemberMutation(options: VueApolloComposable.UseMutationOptions<UpdateGroupMemberMutation, UpdateGroupMemberMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<UpdateGroupMemberMutation, UpdateGroupMemberMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<UpdateGroupMemberMutation, UpdateGroupMemberMutationVariables>(UpdateGroupMemberDocument, options);
}
export type UpdateGroupMemberMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<UpdateGroupMemberMutation, UpdateGroupMemberMutationVariables>;
export const RemoveGroupMemberDocument = gql`
    mutation RemoveGroupMember($memberId: ID!) {
  removeGroupMember(memberId: $memberId) {
    ...GroupMemberBase
  }
}
    ${GroupMemberBaseFragmentDoc}`;

/**
 * __useRemoveGroupMemberMutation__
 *
 * To run a mutation, you first call `useRemoveGroupMemberMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useRemoveGroupMemberMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useRemoveGroupMemberMutation({
 *   variables: {
 *     memberId: // value for 'memberId'
 *   },
 * });
 */
export function useRemoveGroupMemberMutation(options: VueApolloComposable.UseMutationOptions<RemoveGroupMemberMutation, RemoveGroupMemberMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<RemoveGroupMemberMutation, RemoveGroupMemberMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<RemoveGroupMemberMutation, RemoveGroupMemberMutationVariables>(RemoveGroupMemberDocument, options);
}
export type RemoveGroupMemberMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<RemoveGroupMemberMutation, RemoveGroupMemberMutationVariables>;
export const LeaveGroupDocument = gql`
    mutation LeaveGroup($groupId: ID!) {
  leaveGroup(groupId: $groupId) {
    id
  }
}
    `;

/**
 * __useLeaveGroupMutation__
 *
 * To run a mutation, you first call `useLeaveGroupMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useLeaveGroupMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useLeaveGroupMutation({
 *   variables: {
 *     groupId: // value for 'groupId'
 *   },
 * });
 */
export function useLeaveGroupMutation(options: VueApolloComposable.UseMutationOptions<LeaveGroupMutation, LeaveGroupMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<LeaveGroupMutation, LeaveGroupMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<LeaveGroupMutation, LeaveGroupMutationVariables>(LeaveGroupDocument, options);
}
export type LeaveGroupMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<LeaveGroupMutation, LeaveGroupMutationVariables>;
export const InviteToGroupDocument = gql`
    mutation InviteToGroup($groupId: ID!, $usernameOrId: ID!, $role: GroupRole!, $observer: Boolean!, $memberId: ID) {
  inviteToGroup(
    groupId: $groupId
    usernameOrId: $usernameOrId
    role: $role
    observer: $observer
    memberId: $memberId
  ) {
    ...GroupInviteAdmin
  }
}
    ${GroupInviteAdminFragmentDoc}`;

/**
 * __useInviteToGroupMutation__
 *
 * To run a mutation, you first call `useInviteToGroupMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useInviteToGroupMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useInviteToGroupMutation({
 *   variables: {
 *     groupId: // value for 'groupId'
 *     usernameOrId: // value for 'usernameOrId'
 *     role: // value for 'role'
 *     observer: // value for 'observer'
 *     memberId: // value for 'memberId'
 *   },
 * });
 */
export function useInviteToGroupMutation(options: VueApolloComposable.UseMutationOptions<InviteToGroupMutation, InviteToGroupMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<InviteToGroupMutation, InviteToGroupMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<InviteToGroupMutation, InviteToGroupMutationVariables>(InviteToGroupDocument, options);
}
export type InviteToGroupMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<InviteToGroupMutation, InviteToGroupMutationVariables>;
export const CancelGroupInviteDocument = gql`
    mutation CancelGroupInvite($inviteId: ID!) {
  cancelGroupInvite(inviteId: $inviteId) {
    id
    status
  }
}
    `;

/**
 * __useCancelGroupInviteMutation__
 *
 * To run a mutation, you first call `useCancelGroupInviteMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useCancelGroupInviteMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useCancelGroupInviteMutation({
 *   variables: {
 *     inviteId: // value for 'inviteId'
 *   },
 * });
 */
export function useCancelGroupInviteMutation(options: VueApolloComposable.UseMutationOptions<CancelGroupInviteMutation, CancelGroupInviteMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<CancelGroupInviteMutation, CancelGroupInviteMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<CancelGroupInviteMutation, CancelGroupInviteMutationVariables>(CancelGroupInviteDocument, options);
}
export type CancelGroupInviteMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<CancelGroupInviteMutation, CancelGroupInviteMutationVariables>;
export const SetGroupJoinCodeDocument = gql`
    mutation SetGroupJoinCode($groupId: ID!) {
  setGroupJoinCode(groupId: $groupId) {
    id
    joinCode
  }
}
    `;

/**
 * __useSetGroupJoinCodeMutation__
 *
 * To run a mutation, you first call `useSetGroupJoinCodeMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useSetGroupJoinCodeMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useSetGroupJoinCodeMutation({
 *   variables: {
 *     groupId: // value for 'groupId'
 *   },
 * });
 */
export function useSetGroupJoinCodeMutation(options: VueApolloComposable.UseMutationOptions<SetGroupJoinCodeMutation, SetGroupJoinCodeMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<SetGroupJoinCodeMutation, SetGroupJoinCodeMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<SetGroupJoinCodeMutation, SetGroupJoinCodeMutationVariables>(SetGroupJoinCodeDocument, options);
}
export type SetGroupJoinCodeMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<SetGroupJoinCodeMutation, SetGroupJoinCodeMutationVariables>;
export const ClearGroupJoinCodeDocument = gql`
    mutation ClearGroupJoinCode($groupId: ID!) {
  clearGroupJoinCode(groupId: $groupId) {
    id
    joinCode
  }
}
    `;

/**
 * __useClearGroupJoinCodeMutation__
 *
 * To run a mutation, you first call `useClearGroupJoinCodeMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useClearGroupJoinCodeMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useClearGroupJoinCodeMutation({
 *   variables: {
 *     groupId: // value for 'groupId'
 *   },
 * });
 */
export function useClearGroupJoinCodeMutation(options: VueApolloComposable.UseMutationOptions<ClearGroupJoinCodeMutation, ClearGroupJoinCodeMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<ClearGroupJoinCodeMutation, ClearGroupJoinCodeMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<ClearGroupJoinCodeMutation, ClearGroupJoinCodeMutationVariables>(ClearGroupJoinCodeDocument, options);
}
export type ClearGroupJoinCodeMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<ClearGroupJoinCodeMutation, ClearGroupJoinCodeMutationVariables>;
export const RespondToGroupJoinRequestDocument = gql`
    mutation RespondToGroupJoinRequest($inviteId: ID!, $accept: Boolean!, $memberId: ID) {
  respondToGroupJoinRequest(
    inviteId: $inviteId
    accept: $accept
    memberId: $memberId
  ) {
    id
    status
    member {
      ...GroupMemberBase
    }
  }
}
    ${GroupMemberBaseFragmentDoc}`;

/**
 * __useRespondToGroupJoinRequestMutation__
 *
 * To run a mutation, you first call `useRespondToGroupJoinRequestMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useRespondToGroupJoinRequestMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useRespondToGroupJoinRequestMutation({
 *   variables: {
 *     inviteId: // value for 'inviteId'
 *     accept: // value for 'accept'
 *     memberId: // value for 'memberId'
 *   },
 * });
 */
export function useRespondToGroupJoinRequestMutation(options: VueApolloComposable.UseMutationOptions<RespondToGroupJoinRequestMutation, RespondToGroupJoinRequestMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<RespondToGroupJoinRequestMutation, RespondToGroupJoinRequestMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<RespondToGroupJoinRequestMutation, RespondToGroupJoinRequestMutationVariables>(RespondToGroupJoinRequestDocument, options);
}
export type RespondToGroupJoinRequestMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<RespondToGroupJoinRequestMutation, RespondToGroupJoinRequestMutationVariables>;
export const SetProfileOptionsDocument = gql`
    mutation SetProfileOptions($data: ProfileOptionsInput!) {
  setProfileOptions(data: $data) {
    id
    profile {
      public
      checklist
      speed
    }
  }
}
    `;

/**
 * __useSetProfileOptionsMutation__
 *
 * To run a mutation, you first call `useSetProfileOptionsMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useSetProfileOptionsMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useSetProfileOptionsMutation({
 *   variables: {
 *     data: // value for 'data'
 *   },
 * });
 */
export function useSetProfileOptionsMutation(options: VueApolloComposable.UseMutationOptions<SetProfileOptionsMutation, SetProfileOptionsMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<SetProfileOptionsMutation, SetProfileOptionsMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<SetProfileOptionsMutation, SetProfileOptionsMutationVariables>(SetProfileOptionsDocument, options);
}
export type SetProfileOptionsMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<SetProfileOptionsMutation, SetProfileOptionsMutationVariables>;
export const SetSpeedResultGroupDocument = gql`
    mutation SetSpeedResultGroup($speedResultId: ID!, $data: SpeedResultGroupInput!) {
  setSpeedResultGroup(speedResultId: $speedResultId, data: $data) {
    ...SpeedResultBase
    ...SpeedResultSharing
  }
}
    ${SpeedResultBaseFragmentDoc}
${SpeedResultSharingFragmentDoc}`;

/**
 * __useSetSpeedResultGroupMutation__
 *
 * To run a mutation, you first call `useSetSpeedResultGroupMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useSetSpeedResultGroupMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useSetSpeedResultGroupMutation({
 *   variables: {
 *     speedResultId: // value for 'speedResultId'
 *     data: // value for 'data'
 *   },
 * });
 */
export function useSetSpeedResultGroupMutation(options: VueApolloComposable.UseMutationOptions<SetSpeedResultGroupMutation, SetSpeedResultGroupMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<SetSpeedResultGroupMutation, SetSpeedResultGroupMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<SetSpeedResultGroupMutation, SetSpeedResultGroupMutationVariables>(SetSpeedResultGroupDocument, options);
}
export type SetSpeedResultGroupMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<SetSpeedResultGroupMutation, SetSpeedResultGroupMutationVariables>;
export const SetUserLangDocument = gql`
    mutation SetUserLang($lang: String) {
  setUserLang(lang: $lang) {
    id
    lang
  }
}
    `;

/**
 * __useSetUserLangMutation__
 *
 * To run a mutation, you first call `useSetUserLangMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useSetUserLangMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useSetUserLangMutation({
 *   variables: {
 *     lang: // value for 'lang'
 *   },
 * });
 */
export function useSetUserLangMutation(options: VueApolloComposable.UseMutationOptions<SetUserLangMutation, SetUserLangMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<SetUserLangMutation, SetUserLangMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<SetUserLangMutation, SetUserLangMutationVariables>(SetUserLangDocument, options);
}
export type SetUserLangMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<SetUserLangMutation, SetUserLangMutationVariables>;
export const SetUserThemeDocument = gql`
    mutation SetUserTheme($theme: Theme) {
  setUserTheme(theme: $theme) {
    id
    theme
  }
}
    `;

/**
 * __useSetUserThemeMutation__
 *
 * To run a mutation, you first call `useSetUserThemeMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useSetUserThemeMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useSetUserThemeMutation({
 *   variables: {
 *     theme: // value for 'theme'
 *   },
 * });
 */
export function useSetUserThemeMutation(options: VueApolloComposable.UseMutationOptions<SetUserThemeMutation, SetUserThemeMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<SetUserThemeMutation, SetUserThemeMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<SetUserThemeMutation, SetUserThemeMutationVariables>(SetUserThemeDocument, options);
}
export type SetUserThemeMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<SetUserThemeMutation, SetUserThemeMutationVariables>;
export const UpdateSpeedResultDocument = gql`
    mutation UpdateSpeedResult($speedResultId: ID!, $data: SpeedResultUpdateInput!) {
  updateSpeedResult(speedResultId: $speedResultId, data: $data) {
    ...SpeedResultBase
    ...SpeedResultSegments
  }
}
    ${SpeedResultBaseFragmentDoc}
${SpeedResultSegmentsFragmentDoc}`;

/**
 * __useUpdateSpeedResultMutation__
 *
 * To run a mutation, you first call `useUpdateSpeedResultMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useUpdateSpeedResultMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useUpdateSpeedResultMutation({
 *   variables: {
 *     speedResultId: // value for 'speedResultId'
 *     data: // value for 'data'
 *   },
 * });
 */
export function useUpdateSpeedResultMutation(options: VueApolloComposable.UseMutationOptions<UpdateSpeedResultMutation, UpdateSpeedResultMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<UpdateSpeedResultMutation, UpdateSpeedResultMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<UpdateSpeedResultMutation, UpdateSpeedResultMutationVariables>(UpdateSpeedResultDocument, options);
}
export type UpdateSpeedResultMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<UpdateSpeedResultMutation, UpdateSpeedResultMutationVariables>;
export const UpdateUserProfileDocument = gql`
    mutation UpdateUserProfile($data: UserProfileInput!) {
  updateUserProfile(data: $data) {
    id
    username
    name
    photo
  }
}
    `;

/**
 * __useUpdateUserProfileMutation__
 *
 * To run a mutation, you first call `useUpdateUserProfileMutation` within a Vue component and pass it any options that fit your needs.
 * When your component renders, `useUpdateUserProfileMutation` returns an object that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - Several other properties: https://v4.apollo.vuejs.org/api/use-mutation.html#return
 *
 * @param options that will be passed into the mutation, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/mutation.html#options;
 *
 * @example
 * const { mutate, loading, error, onDone } = useUpdateUserProfileMutation({
 *   variables: {
 *     data: // value for 'data'
 *   },
 * });
 */
export function useUpdateUserProfileMutation(options: VueApolloComposable.UseMutationOptions<UpdateUserProfileMutation, UpdateUserProfileMutationVariables> | ReactiveFunction<VueApolloComposable.UseMutationOptions<UpdateUserProfileMutation, UpdateUserProfileMutationVariables>> = {}) {
  return VueApolloComposable.useMutation<UpdateUserProfileMutation, UpdateUserProfileMutationVariables>(UpdateUserProfileDocument, options);
}
export type UpdateUserProfileMutationCompositionFunctionResult = VueApolloComposable.UseMutationReturn<UpdateUserProfileMutation, UpdateUserProfileMutationVariables>;
export const EventDefinitionsDocument = gql`
    query EventDefinitions {
  eventDefinitions {
    id
    name
    totalDuration
    eventDefinitionLookupCode
    timingTrack {
      ...TimingTrackFields
    }
  }
}
    ${TimingTrackFieldsFragmentDoc}`;

/**
 * __useEventDefinitionsQuery__
 *
 * To run a query within a Vue component, call `useEventDefinitionsQuery` and pass it any options that fit your needs.
 * When your component renders, `useEventDefinitionsQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useEventDefinitionsQuery();
 */
export function useEventDefinitionsQuery(options: VueApolloComposable.UseQueryOptions<EventDefinitionsQuery, EventDefinitionsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<EventDefinitionsQuery, EventDefinitionsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<EventDefinitionsQuery, EventDefinitionsQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<EventDefinitionsQuery, EventDefinitionsQueryVariables>(EventDefinitionsDocument, {}, options);
}
export function useEventDefinitionsLazyQuery(options: VueApolloComposable.UseQueryOptions<EventDefinitionsQuery, EventDefinitionsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<EventDefinitionsQuery, EventDefinitionsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<EventDefinitionsQuery, EventDefinitionsQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<EventDefinitionsQuery, EventDefinitionsQueryVariables>(EventDefinitionsDocument, {}, options);
}
export type EventDefinitionsQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<EventDefinitionsQuery, EventDefinitionsQueryVariables>;
export const GlobalStatsDocument = gql`
    query GlobalStats($from: Timestamp) {
  globalStats {
    countedAt
    tricks
    completions
    averageCompletions
    acceptedSubmissions
    levels {
      level
      tricks
      averageCompletions
    }
  }
  globalStatsHistory(from: $from) {
    countedAt
    speedResults
    speedSteps
    levels {
      level
      completions
    }
  }
}
    `;

/**
 * __useGlobalStatsQuery__
 *
 * To run a query within a Vue component, call `useGlobalStatsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGlobalStatsQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param variables that will be passed into the query
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useGlobalStatsQuery({
 *   from: // value for 'from'
 * });
 */
export function useGlobalStatsQuery(variables: GlobalStatsQueryVariables | VueCompositionApi.Ref<GlobalStatsQueryVariables> | ReactiveFunction<GlobalStatsQueryVariables> = {}, options: VueApolloComposable.UseQueryOptions<GlobalStatsQuery, GlobalStatsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<GlobalStatsQuery, GlobalStatsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<GlobalStatsQuery, GlobalStatsQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<GlobalStatsQuery, GlobalStatsQueryVariables>(GlobalStatsDocument, variables, options);
}
export function useGlobalStatsLazyQuery(variables: GlobalStatsQueryVariables | VueCompositionApi.Ref<GlobalStatsQueryVariables> | ReactiveFunction<GlobalStatsQueryVariables> = {}, options: VueApolloComposable.UseQueryOptions<GlobalStatsQuery, GlobalStatsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<GlobalStatsQuery, GlobalStatsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<GlobalStatsQuery, GlobalStatsQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<GlobalStatsQuery, GlobalStatsQueryVariables>(GlobalStatsDocument, variables, options);
}
export type GlobalStatsQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<GlobalStatsQuery, GlobalStatsQueryVariables>;
export const GroupDocument = gql`
    query Group($groupId: ID!) {
  group(groupId: $groupId) {
    id
    name
    myMembership {
      id
      role
      observer
    }
  }
}
    `;

/**
 * __useGroupQuery__
 *
 * To run a query within a Vue component, call `useGroupQuery` and pass it any options that fit your needs.
 * When your component renders, `useGroupQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param variables that will be passed into the query
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useGroupQuery({
 *   groupId: // value for 'groupId'
 * });
 */
export function useGroupQuery(variables: GroupQueryVariables | VueCompositionApi.Ref<GroupQueryVariables> | ReactiveFunction<GroupQueryVariables>, options: VueApolloComposable.UseQueryOptions<GroupQuery, GroupQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<GroupQuery, GroupQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<GroupQuery, GroupQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<GroupQuery, GroupQueryVariables>(GroupDocument, variables, options);
}
export function useGroupLazyQuery(variables?: GroupQueryVariables | VueCompositionApi.Ref<GroupQueryVariables> | ReactiveFunction<GroupQueryVariables>, options: VueApolloComposable.UseQueryOptions<GroupQuery, GroupQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<GroupQuery, GroupQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<GroupQuery, GroupQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<GroupQuery, GroupQueryVariables>(GroupDocument, variables, options);
}
export type GroupQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<GroupQuery, GroupQueryVariables>;
export const GroupMembersDocument = gql`
    query GroupMembers($groupId: ID!) {
  group(groupId: $groupId) {
    id
    myMembership {
      id
      role
      observer
    }
    members {
      ...GroupMemberBase
    }
  }
}
    ${GroupMemberBaseFragmentDoc}`;

/**
 * __useGroupMembersQuery__
 *
 * To run a query within a Vue component, call `useGroupMembersQuery` and pass it any options that fit your needs.
 * When your component renders, `useGroupMembersQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param variables that will be passed into the query
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useGroupMembersQuery({
 *   groupId: // value for 'groupId'
 * });
 */
export function useGroupMembersQuery(variables: GroupMembersQueryVariables | VueCompositionApi.Ref<GroupMembersQueryVariables> | ReactiveFunction<GroupMembersQueryVariables>, options: VueApolloComposable.UseQueryOptions<GroupMembersQuery, GroupMembersQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<GroupMembersQuery, GroupMembersQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<GroupMembersQuery, GroupMembersQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<GroupMembersQuery, GroupMembersQueryVariables>(GroupMembersDocument, variables, options);
}
export function useGroupMembersLazyQuery(variables?: GroupMembersQueryVariables | VueCompositionApi.Ref<GroupMembersQueryVariables> | ReactiveFunction<GroupMembersQueryVariables>, options: VueApolloComposable.UseQueryOptions<GroupMembersQuery, GroupMembersQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<GroupMembersQuery, GroupMembersQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<GroupMembersQuery, GroupMembersQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<GroupMembersQuery, GroupMembersQueryVariables>(GroupMembersDocument, variables, options);
}
export type GroupMembersQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<GroupMembersQuery, GroupMembersQueryVariables>;
export const GroupAdminDocument = gql`
    query GroupAdmin($groupId: ID!) {
  group(groupId: $groupId) {
    id
    joinCode
    invites {
      ...GroupInviteAdmin
    }
  }
}
    ${GroupInviteAdminFragmentDoc}`;

/**
 * __useGroupAdminQuery__
 *
 * To run a query within a Vue component, call `useGroupAdminQuery` and pass it any options that fit your needs.
 * When your component renders, `useGroupAdminQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param variables that will be passed into the query
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useGroupAdminQuery({
 *   groupId: // value for 'groupId'
 * });
 */
export function useGroupAdminQuery(variables: GroupAdminQueryVariables | VueCompositionApi.Ref<GroupAdminQueryVariables> | ReactiveFunction<GroupAdminQueryVariables>, options: VueApolloComposable.UseQueryOptions<GroupAdminQuery, GroupAdminQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<GroupAdminQuery, GroupAdminQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<GroupAdminQuery, GroupAdminQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<GroupAdminQuery, GroupAdminQueryVariables>(GroupAdminDocument, variables, options);
}
export function useGroupAdminLazyQuery(variables?: GroupAdminQueryVariables | VueCompositionApi.Ref<GroupAdminQueryVariables> | ReactiveFunction<GroupAdminQueryVariables>, options: VueApolloComposable.UseQueryOptions<GroupAdminQuery, GroupAdminQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<GroupAdminQuery, GroupAdminQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<GroupAdminQuery, GroupAdminQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<GroupAdminQuery, GroupAdminQueryVariables>(GroupAdminDocument, variables, options);
}
export type GroupAdminQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<GroupAdminQuery, GroupAdminQueryVariables>;
export const GroupChecklistsDocument = gql`
    query GroupChecklists($groupId: ID!, $discipline: Discipline, $withLocalised: Boolean!, $lang: String) {
  group(groupId: $groupId) {
    id
    myMembership {
      id
      role
    }
    members {
      id
      name
      observer
      user {
        id
      }
      checklist {
        id
        trick {
          id
        }
      }
    }
  }
  tricks(discipline: $discipline) {
    ...TrickBox
  }
}
    ${TrickBoxFragmentDoc}`;

/**
 * __useGroupChecklistsQuery__
 *
 * To run a query within a Vue component, call `useGroupChecklistsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGroupChecklistsQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param variables that will be passed into the query
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useGroupChecklistsQuery({
 *   groupId: // value for 'groupId'
 *   discipline: // value for 'discipline'
 *   withLocalised: // value for 'withLocalised'
 *   lang: // value for 'lang'
 * });
 */
export function useGroupChecklistsQuery(variables: GroupChecklistsQueryVariables | VueCompositionApi.Ref<GroupChecklistsQueryVariables> | ReactiveFunction<GroupChecklistsQueryVariables>, options: VueApolloComposable.UseQueryOptions<GroupChecklistsQuery, GroupChecklistsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<GroupChecklistsQuery, GroupChecklistsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<GroupChecklistsQuery, GroupChecklistsQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<GroupChecklistsQuery, GroupChecklistsQueryVariables>(GroupChecklistsDocument, variables, options);
}
export function useGroupChecklistsLazyQuery(variables?: GroupChecklistsQueryVariables | VueCompositionApi.Ref<GroupChecklistsQueryVariables> | ReactiveFunction<GroupChecklistsQueryVariables>, options: VueApolloComposable.UseQueryOptions<GroupChecklistsQuery, GroupChecklistsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<GroupChecklistsQuery, GroupChecklistsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<GroupChecklistsQuery, GroupChecklistsQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<GroupChecklistsQuery, GroupChecklistsQueryVariables>(GroupChecklistsDocument, variables, options);
}
export type GroupChecklistsQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<GroupChecklistsQuery, GroupChecklistsQueryVariables>;
export const GroupSpeedResultsDocument = gql`
    query GroupSpeedResults($groupId: ID!, $limit: Int, $startAfter: Timestamp, $eventDefinitionId: ID, $constellation: [ID!]) {
  group(groupId: $groupId) {
    id
    speedResults(
      limit: $limit
      startAfter: $startAfter
      eventDefinitionId: $eventDefinitionId
      constellation: $constellation
    ) {
      ...SpeedResultBase
    }
  }
}
    ${SpeedResultBaseFragmentDoc}`;

/**
 * __useGroupSpeedResultsQuery__
 *
 * To run a query within a Vue component, call `useGroupSpeedResultsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGroupSpeedResultsQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param variables that will be passed into the query
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useGroupSpeedResultsQuery({
 *   groupId: // value for 'groupId'
 *   limit: // value for 'limit'
 *   startAfter: // value for 'startAfter'
 *   eventDefinitionId: // value for 'eventDefinitionId'
 *   constellation: // value for 'constellation'
 * });
 */
export function useGroupSpeedResultsQuery(variables: GroupSpeedResultsQueryVariables | VueCompositionApi.Ref<GroupSpeedResultsQueryVariables> | ReactiveFunction<GroupSpeedResultsQueryVariables>, options: VueApolloComposable.UseQueryOptions<GroupSpeedResultsQuery, GroupSpeedResultsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<GroupSpeedResultsQuery, GroupSpeedResultsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<GroupSpeedResultsQuery, GroupSpeedResultsQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<GroupSpeedResultsQuery, GroupSpeedResultsQueryVariables>(GroupSpeedResultsDocument, variables, options);
}
export function useGroupSpeedResultsLazyQuery(variables?: GroupSpeedResultsQueryVariables | VueCompositionApi.Ref<GroupSpeedResultsQueryVariables> | ReactiveFunction<GroupSpeedResultsQueryVariables>, options: VueApolloComposable.UseQueryOptions<GroupSpeedResultsQuery, GroupSpeedResultsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<GroupSpeedResultsQuery, GroupSpeedResultsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<GroupSpeedResultsQuery, GroupSpeedResultsQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<GroupSpeedResultsQuery, GroupSpeedResultsQueryVariables>(GroupSpeedResultsDocument, variables, options);
}
export type GroupSpeedResultsQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<GroupSpeedResultsQuery, GroupSpeedResultsQueryVariables>;
export const GroupSpeedAnalysisDocument = gql`
    query GroupSpeedAnalysis($groupId: ID!, $limit: Int, $startAfter: Timestamp, $eventDefinitionId: ID, $constellation: [ID!]) {
  group(groupId: $groupId) {
    id
    speedResults(
      limit: $limit
      startAfter: $startAfter
      eventDefinitionId: $eventDefinitionId
      constellation: $constellation
    ) {
      ...SpeedResultBase
      analysis {
        stepsPerSecond
      }
      segments {
        ...SpeedSegmentFields
      }
    }
  }
}
    ${SpeedResultBaseFragmentDoc}
${SpeedSegmentFieldsFragmentDoc}`;

/**
 * __useGroupSpeedAnalysisQuery__
 *
 * To run a query within a Vue component, call `useGroupSpeedAnalysisQuery` and pass it any options that fit your needs.
 * When your component renders, `useGroupSpeedAnalysisQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param variables that will be passed into the query
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useGroupSpeedAnalysisQuery({
 *   groupId: // value for 'groupId'
 *   limit: // value for 'limit'
 *   startAfter: // value for 'startAfter'
 *   eventDefinitionId: // value for 'eventDefinitionId'
 *   constellation: // value for 'constellation'
 * });
 */
export function useGroupSpeedAnalysisQuery(variables: GroupSpeedAnalysisQueryVariables | VueCompositionApi.Ref<GroupSpeedAnalysisQueryVariables> | ReactiveFunction<GroupSpeedAnalysisQueryVariables>, options: VueApolloComposable.UseQueryOptions<GroupSpeedAnalysisQuery, GroupSpeedAnalysisQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<GroupSpeedAnalysisQuery, GroupSpeedAnalysisQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<GroupSpeedAnalysisQuery, GroupSpeedAnalysisQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<GroupSpeedAnalysisQuery, GroupSpeedAnalysisQueryVariables>(GroupSpeedAnalysisDocument, variables, options);
}
export function useGroupSpeedAnalysisLazyQuery(variables?: GroupSpeedAnalysisQueryVariables | VueCompositionApi.Ref<GroupSpeedAnalysisQueryVariables> | ReactiveFunction<GroupSpeedAnalysisQueryVariables>, options: VueApolloComposable.UseQueryOptions<GroupSpeedAnalysisQuery, GroupSpeedAnalysisQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<GroupSpeedAnalysisQuery, GroupSpeedAnalysisQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<GroupSpeedAnalysisQuery, GroupSpeedAnalysisQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<GroupSpeedAnalysisQuery, GroupSpeedAnalysisQueryVariables>(GroupSpeedAnalysisDocument, variables, options);
}
export type GroupSpeedAnalysisQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<GroupSpeedAnalysisQuery, GroupSpeedAnalysisQueryVariables>;
export const GroupConstellationsDocument = gql`
    query GroupConstellations($groupId: ID!) {
  group(groupId: $groupId) {
    id
    constellations {
      key
      resultCount
      members {
        id
        name
      }
    }
  }
}
    `;

/**
 * __useGroupConstellationsQuery__
 *
 * To run a query within a Vue component, call `useGroupConstellationsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGroupConstellationsQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param variables that will be passed into the query
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useGroupConstellationsQuery({
 *   groupId: // value for 'groupId'
 * });
 */
export function useGroupConstellationsQuery(variables: GroupConstellationsQueryVariables | VueCompositionApi.Ref<GroupConstellationsQueryVariables> | ReactiveFunction<GroupConstellationsQueryVariables>, options: VueApolloComposable.UseQueryOptions<GroupConstellationsQuery, GroupConstellationsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<GroupConstellationsQuery, GroupConstellationsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<GroupConstellationsQuery, GroupConstellationsQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<GroupConstellationsQuery, GroupConstellationsQueryVariables>(GroupConstellationsDocument, variables, options);
}
export function useGroupConstellationsLazyQuery(variables?: GroupConstellationsQueryVariables | VueCompositionApi.Ref<GroupConstellationsQueryVariables> | ReactiveFunction<GroupConstellationsQueryVariables>, options: VueApolloComposable.UseQueryOptions<GroupConstellationsQuery, GroupConstellationsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<GroupConstellationsQuery, GroupConstellationsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<GroupConstellationsQuery, GroupConstellationsQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<GroupConstellationsQuery, GroupConstellationsQueryVariables>(GroupConstellationsDocument, variables, options);
}
export type GroupConstellationsQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<GroupConstellationsQuery, GroupConstellationsQueryVariables>;
export const GroupMemberBestsDocument = gql`
    query GroupMemberBests($memberId: ID!) {
  groupMember(memberId: $memberId) {
    id
    speedBests {
      ...SpeedPersonalBestBase
    }
  }
}
    ${SpeedPersonalBestBaseFragmentDoc}`;

/**
 * __useGroupMemberBestsQuery__
 *
 * To run a query within a Vue component, call `useGroupMemberBestsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGroupMemberBestsQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param variables that will be passed into the query
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useGroupMemberBestsQuery({
 *   memberId: // value for 'memberId'
 * });
 */
export function useGroupMemberBestsQuery(variables: GroupMemberBestsQueryVariables | VueCompositionApi.Ref<GroupMemberBestsQueryVariables> | ReactiveFunction<GroupMemberBestsQueryVariables>, options: VueApolloComposable.UseQueryOptions<GroupMemberBestsQuery, GroupMemberBestsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<GroupMemberBestsQuery, GroupMemberBestsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<GroupMemberBestsQuery, GroupMemberBestsQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<GroupMemberBestsQuery, GroupMemberBestsQueryVariables>(GroupMemberBestsDocument, variables, options);
}
export function useGroupMemberBestsLazyQuery(variables?: GroupMemberBestsQueryVariables | VueCompositionApi.Ref<GroupMemberBestsQueryVariables> | ReactiveFunction<GroupMemberBestsQueryVariables>, options: VueApolloComposable.UseQueryOptions<GroupMemberBestsQuery, GroupMemberBestsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<GroupMemberBestsQuery, GroupMemberBestsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<GroupMemberBestsQuery, GroupMemberBestsQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<GroupMemberBestsQuery, GroupMemberBestsQueryVariables>(GroupMemberBestsDocument, variables, options);
}
export type GroupMemberBestsQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<GroupMemberBestsQuery, GroupMemberBestsQueryVariables>;
export const MyGroupsDocument = gql`
    query MyGroups {
  me {
    id
    groups {
      ...GroupBase
    }
  }
}
    ${GroupBaseFragmentDoc}`;

/**
 * __useMyGroupsQuery__
 *
 * To run a query within a Vue component, call `useMyGroupsQuery` and pass it any options that fit your needs.
 * When your component renders, `useMyGroupsQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useMyGroupsQuery();
 */
export function useMyGroupsQuery(options: VueApolloComposable.UseQueryOptions<MyGroupsQuery, MyGroupsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<MyGroupsQuery, MyGroupsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<MyGroupsQuery, MyGroupsQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<MyGroupsQuery, MyGroupsQueryVariables>(MyGroupsDocument, {}, options);
}
export function useMyGroupsLazyQuery(options: VueApolloComposable.UseQueryOptions<MyGroupsQuery, MyGroupsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<MyGroupsQuery, MyGroupsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<MyGroupsQuery, MyGroupsQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<MyGroupsQuery, MyGroupsQueryVariables>(MyGroupsDocument, {}, options);
}
export type MyGroupsQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<MyGroupsQuery, MyGroupsQueryVariables>;
export const GroupInvitesDocument = gql`
    query GroupInvites {
  me {
    id
    groupInvites {
      ...GroupInviteBase
    }
  }
}
    ${GroupInviteBaseFragmentDoc}`;

/**
 * __useGroupInvitesQuery__
 *
 * To run a query within a Vue component, call `useGroupInvitesQuery` and pass it any options that fit your needs.
 * When your component renders, `useGroupInvitesQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useGroupInvitesQuery();
 */
export function useGroupInvitesQuery(options: VueApolloComposable.UseQueryOptions<GroupInvitesQuery, GroupInvitesQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<GroupInvitesQuery, GroupInvitesQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<GroupInvitesQuery, GroupInvitesQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<GroupInvitesQuery, GroupInvitesQueryVariables>(GroupInvitesDocument, {}, options);
}
export function useGroupInvitesLazyQuery(options: VueApolloComposable.UseQueryOptions<GroupInvitesQuery, GroupInvitesQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<GroupInvitesQuery, GroupInvitesQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<GroupInvitesQuery, GroupInvitesQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<GroupInvitesQuery, GroupInvitesQueryVariables>(GroupInvitesDocument, {}, options);
}
export type GroupInvitesQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<GroupInvitesQuery, GroupInvitesQueryVariables>;
export const GroupByJoinCodeDocument = gql`
    query GroupByJoinCode($joinCode: String!) {
  groupByJoinCode(joinCode: $joinCode) {
    id
    name
  }
}
    `;

/**
 * __useGroupByJoinCodeQuery__
 *
 * To run a query within a Vue component, call `useGroupByJoinCodeQuery` and pass it any options that fit your needs.
 * When your component renders, `useGroupByJoinCodeQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param variables that will be passed into the query
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useGroupByJoinCodeQuery({
 *   joinCode: // value for 'joinCode'
 * });
 */
export function useGroupByJoinCodeQuery(variables: GroupByJoinCodeQueryVariables | VueCompositionApi.Ref<GroupByJoinCodeQueryVariables> | ReactiveFunction<GroupByJoinCodeQueryVariables>, options: VueApolloComposable.UseQueryOptions<GroupByJoinCodeQuery, GroupByJoinCodeQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<GroupByJoinCodeQuery, GroupByJoinCodeQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<GroupByJoinCodeQuery, GroupByJoinCodeQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<GroupByJoinCodeQuery, GroupByJoinCodeQueryVariables>(GroupByJoinCodeDocument, variables, options);
}
export function useGroupByJoinCodeLazyQuery(variables?: GroupByJoinCodeQueryVariables | VueCompositionApi.Ref<GroupByJoinCodeQueryVariables> | ReactiveFunction<GroupByJoinCodeQueryVariables>, options: VueApolloComposable.UseQueryOptions<GroupByJoinCodeQuery, GroupByJoinCodeQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<GroupByJoinCodeQuery, GroupByJoinCodeQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<GroupByJoinCodeQuery, GroupByJoinCodeQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<GroupByJoinCodeQuery, GroupByJoinCodeQueryVariables>(GroupByJoinCodeDocument, variables, options);
}
export type GroupByJoinCodeQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<GroupByJoinCodeQuery, GroupByJoinCodeQueryVariables>;
export const LanguagesDocument = gql`
    query Languages {
  languages {
    id
    enabled
  }
}
    `;

/**
 * __useLanguagesQuery__
 *
 * To run a query within a Vue component, call `useLanguagesQuery` and pass it any options that fit your needs.
 * When your component renders, `useLanguagesQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useLanguagesQuery();
 */
export function useLanguagesQuery(options: VueApolloComposable.UseQueryOptions<LanguagesQuery, LanguagesQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<LanguagesQuery, LanguagesQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<LanguagesQuery, LanguagesQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<LanguagesQuery, LanguagesQueryVariables>(LanguagesDocument, {}, options);
}
export function useLanguagesLazyQuery(options: VueApolloComposable.UseQueryOptions<LanguagesQuery, LanguagesQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<LanguagesQuery, LanguagesQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<LanguagesQuery, LanguagesQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<LanguagesQuery, LanguagesQueryVariables>(LanguagesDocument, {}, options);
}
export type LanguagesQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<LanguagesQuery, LanguagesQueryVariables>;
export const MeDocument = gql`
    query Me($withChecklist: Boolean!) {
  me {
    id
    username
    name
    photo
    email
    lang
    theme
    profile {
      public
      checklist
      speed
    }
    checklist @include(if: $withChecklist) {
      id
      createdAt
      trick {
        id
      }
      recordedBy {
        id
        name
      }
    }
  }
}
    `;

/**
 * __useMeQuery__
 *
 * To run a query within a Vue component, call `useMeQuery` and pass it any options that fit your needs.
 * When your component renders, `useMeQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param variables that will be passed into the query
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useMeQuery({
 *   withChecklist: // value for 'withChecklist'
 * });
 */
export function useMeQuery(variables: MeQueryVariables | VueCompositionApi.Ref<MeQueryVariables> | ReactiveFunction<MeQueryVariables>, options: VueApolloComposable.UseQueryOptions<MeQuery, MeQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<MeQuery, MeQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<MeQuery, MeQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<MeQuery, MeQueryVariables>(MeDocument, variables, options);
}
export function useMeLazyQuery(variables?: MeQueryVariables | VueCompositionApi.Ref<MeQueryVariables> | ReactiveFunction<MeQueryVariables>, options: VueApolloComposable.UseQueryOptions<MeQuery, MeQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<MeQuery, MeQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<MeQuery, MeQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<MeQuery, MeQueryVariables>(MeDocument, variables, options);
}
export type MeQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<MeQuery, MeQueryVariables>;
export const NoticesDocument = gql`
    query Notices($lang: String!) {
  notices {
    id
    updatedAt
    text(lang: $lang) {
      lang
      body
      links {
        label
        url
      }
    }
  }
}
    `;

/**
 * __useNoticesQuery__
 *
 * To run a query within a Vue component, call `useNoticesQuery` and pass it any options that fit your needs.
 * When your component renders, `useNoticesQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param variables that will be passed into the query
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useNoticesQuery({
 *   lang: // value for 'lang'
 * });
 */
export function useNoticesQuery(variables: NoticesQueryVariables | VueCompositionApi.Ref<NoticesQueryVariables> | ReactiveFunction<NoticesQueryVariables>, options: VueApolloComposable.UseQueryOptions<NoticesQuery, NoticesQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<NoticesQuery, NoticesQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<NoticesQuery, NoticesQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<NoticesQuery, NoticesQueryVariables>(NoticesDocument, variables, options);
}
export function useNoticesLazyQuery(variables?: NoticesQueryVariables | VueCompositionApi.Ref<NoticesQueryVariables> | ReactiveFunction<NoticesQueryVariables>, options: VueApolloComposable.UseQueryOptions<NoticesQuery, NoticesQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<NoticesQuery, NoticesQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<NoticesQuery, NoticesQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<NoticesQuery, NoticesQueryVariables>(NoticesDocument, variables, options);
}
export type NoticesQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<NoticesQuery, NoticesQueryVariables>;
export const ProductsDocument = gql`
    query Products {
  products {
    id
    name
    description
    image
    prices {
      id
      currency
      unitAmount
    }
  }
  shippingRates {
    id
    currency
    unitAmount
  }
}
    `;

/**
 * __useProductsQuery__
 *
 * To run a query within a Vue component, call `useProductsQuery` and pass it any options that fit your needs.
 * When your component renders, `useProductsQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useProductsQuery();
 */
export function useProductsQuery(options: VueApolloComposable.UseQueryOptions<ProductsQuery, ProductsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<ProductsQuery, ProductsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<ProductsQuery, ProductsQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<ProductsQuery, ProductsQueryVariables>(ProductsDocument, {}, options);
}
export function useProductsLazyQuery(options: VueApolloComposable.UseQueryOptions<ProductsQuery, ProductsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<ProductsQuery, ProductsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<ProductsQuery, ProductsQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<ProductsQuery, ProductsQueryVariables>(ProductsDocument, {}, options);
}
export type ProductsQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<ProductsQuery, ProductsQueryVariables>;
export const ProfileHeaderDocument = gql`
    query ProfileHeader($usernameOrId: ID!) {
  user(usernameOrId: $usernameOrId) {
    ...ProfileHeader
  }
}
    ${ProfileHeaderFragmentDoc}`;

/**
 * __useProfileHeaderQuery__
 *
 * To run a query within a Vue component, call `useProfileHeaderQuery` and pass it any options that fit your needs.
 * When your component renders, `useProfileHeaderQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param variables that will be passed into the query
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useProfileHeaderQuery({
 *   usernameOrId: // value for 'usernameOrId'
 * });
 */
export function useProfileHeaderQuery(variables: ProfileHeaderQueryVariables | VueCompositionApi.Ref<ProfileHeaderQueryVariables> | ReactiveFunction<ProfileHeaderQueryVariables>, options: VueApolloComposable.UseQueryOptions<ProfileHeaderQuery, ProfileHeaderQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<ProfileHeaderQuery, ProfileHeaderQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<ProfileHeaderQuery, ProfileHeaderQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<ProfileHeaderQuery, ProfileHeaderQueryVariables>(ProfileHeaderDocument, variables, options);
}
export function useProfileHeaderLazyQuery(variables?: ProfileHeaderQueryVariables | VueCompositionApi.Ref<ProfileHeaderQueryVariables> | ReactiveFunction<ProfileHeaderQueryVariables>, options: VueApolloComposable.UseQueryOptions<ProfileHeaderQuery, ProfileHeaderQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<ProfileHeaderQuery, ProfileHeaderQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<ProfileHeaderQuery, ProfileHeaderQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<ProfileHeaderQuery, ProfileHeaderQueryVariables>(ProfileHeaderDocument, variables, options);
}
export type ProfileHeaderQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<ProfileHeaderQuery, ProfileHeaderQueryVariables>;
export const ProfileDetailsDocument = gql`
    query ProfileDetails($usernameOrId: ID!, $withChecklist: Boolean!, $withSpeed: Boolean!, $withLocalised: Boolean!, $lang: String) {
  user(usernameOrId: $usernameOrId) {
    ...ProfileUser
  }
}
    ${ProfileUserFragmentDoc}`;

/**
 * __useProfileDetailsQuery__
 *
 * To run a query within a Vue component, call `useProfileDetailsQuery` and pass it any options that fit your needs.
 * When your component renders, `useProfileDetailsQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param variables that will be passed into the query
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useProfileDetailsQuery({
 *   usernameOrId: // value for 'usernameOrId'
 *   withChecklist: // value for 'withChecklist'
 *   withSpeed: // value for 'withSpeed'
 *   withLocalised: // value for 'withLocalised'
 *   lang: // value for 'lang'
 * });
 */
export function useProfileDetailsQuery(variables: ProfileDetailsQueryVariables | VueCompositionApi.Ref<ProfileDetailsQueryVariables> | ReactiveFunction<ProfileDetailsQueryVariables>, options: VueApolloComposable.UseQueryOptions<ProfileDetailsQuery, ProfileDetailsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<ProfileDetailsQuery, ProfileDetailsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<ProfileDetailsQuery, ProfileDetailsQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<ProfileDetailsQuery, ProfileDetailsQueryVariables>(ProfileDetailsDocument, variables, options);
}
export function useProfileDetailsLazyQuery(variables?: ProfileDetailsQueryVariables | VueCompositionApi.Ref<ProfileDetailsQueryVariables> | ReactiveFunction<ProfileDetailsQueryVariables>, options: VueApolloComposable.UseQueryOptions<ProfileDetailsQuery, ProfileDetailsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<ProfileDetailsQuery, ProfileDetailsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<ProfileDetailsQuery, ProfileDetailsQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<ProfileDetailsQuery, ProfileDetailsQueryVariables>(ProfileDetailsDocument, variables, options);
}
export type ProfileDetailsQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<ProfileDetailsQuery, ProfileDetailsQueryVariables>;
export const MyProfileDetailsDocument = gql`
    query MyProfileDetails($withChecklist: Boolean!, $withSpeed: Boolean!, $withLocalised: Boolean!, $lang: String) {
  me {
    ...ProfileUser
  }
}
    ${ProfileUserFragmentDoc}`;

/**
 * __useMyProfileDetailsQuery__
 *
 * To run a query within a Vue component, call `useMyProfileDetailsQuery` and pass it any options that fit your needs.
 * When your component renders, `useMyProfileDetailsQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param variables that will be passed into the query
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useMyProfileDetailsQuery({
 *   withChecklist: // value for 'withChecklist'
 *   withSpeed: // value for 'withSpeed'
 *   withLocalised: // value for 'withLocalised'
 *   lang: // value for 'lang'
 * });
 */
export function useMyProfileDetailsQuery(variables: MyProfileDetailsQueryVariables | VueCompositionApi.Ref<MyProfileDetailsQueryVariables> | ReactiveFunction<MyProfileDetailsQueryVariables>, options: VueApolloComposable.UseQueryOptions<MyProfileDetailsQuery, MyProfileDetailsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<MyProfileDetailsQuery, MyProfileDetailsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<MyProfileDetailsQuery, MyProfileDetailsQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<MyProfileDetailsQuery, MyProfileDetailsQueryVariables>(MyProfileDetailsDocument, variables, options);
}
export function useMyProfileDetailsLazyQuery(variables?: MyProfileDetailsQueryVariables | VueCompositionApi.Ref<MyProfileDetailsQueryVariables> | ReactiveFunction<MyProfileDetailsQueryVariables>, options: VueApolloComposable.UseQueryOptions<MyProfileDetailsQuery, MyProfileDetailsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<MyProfileDetailsQuery, MyProfileDetailsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<MyProfileDetailsQuery, MyProfileDetailsQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<MyProfileDetailsQuery, MyProfileDetailsQueryVariables>(MyProfileDetailsDocument, variables, options);
}
export type MyProfileDetailsQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<MyProfileDetailsQuery, MyProfileDetailsQueryVariables>;
export const RulesetsDocument = gql`
    query Rulesets($lang: String) {
  rulesets {
    id
    name(lang: $lang)
    isPrimary
  }
}
    `;

/**
 * __useRulesetsQuery__
 *
 * To run a query within a Vue component, call `useRulesetsQuery` and pass it any options that fit your needs.
 * When your component renders, `useRulesetsQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param variables that will be passed into the query
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useRulesetsQuery({
 *   lang: // value for 'lang'
 * });
 */
export function useRulesetsQuery(variables: RulesetsQueryVariables | VueCompositionApi.Ref<RulesetsQueryVariables> | ReactiveFunction<RulesetsQueryVariables> = {}, options: VueApolloComposable.UseQueryOptions<RulesetsQuery, RulesetsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<RulesetsQuery, RulesetsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<RulesetsQuery, RulesetsQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<RulesetsQuery, RulesetsQueryVariables>(RulesetsDocument, variables, options);
}
export function useRulesetsLazyQuery(variables: RulesetsQueryVariables | VueCompositionApi.Ref<RulesetsQueryVariables> | ReactiveFunction<RulesetsQueryVariables> = {}, options: VueApolloComposable.UseQueryOptions<RulesetsQuery, RulesetsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<RulesetsQuery, RulesetsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<RulesetsQuery, RulesetsQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<RulesetsQuery, RulesetsQueryVariables>(RulesetsDocument, variables, options);
}
export type RulesetsQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<RulesetsQuery, RulesetsQueryVariables>;
export const SpeedResultDocument = gql`
    query SpeedResult($speedResultId: ID!) {
  me {
    id
    speedResult(speedResultId: $speedResultId) {
      ...SpeedResultBase
      ...SpeedResultSharing
      ...SpeedResultSegments
      excludedFromPersonalBests
      eventDefinition {
        id
        timingTrack {
          cues {
            type
            offset
            label
          }
        }
      }
      analysis {
        ...SpeedAnalysisFields
      }
    }
  }
}
    ${SpeedResultBaseFragmentDoc}
${SpeedResultSharingFragmentDoc}
${SpeedResultSegmentsFragmentDoc}
${SpeedAnalysisFieldsFragmentDoc}`;

/**
 * __useSpeedResultQuery__
 *
 * To run a query within a Vue component, call `useSpeedResultQuery` and pass it any options that fit your needs.
 * When your component renders, `useSpeedResultQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param variables that will be passed into the query
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useSpeedResultQuery({
 *   speedResultId: // value for 'speedResultId'
 * });
 */
export function useSpeedResultQuery(variables: SpeedResultQueryVariables | VueCompositionApi.Ref<SpeedResultQueryVariables> | ReactiveFunction<SpeedResultQueryVariables>, options: VueApolloComposable.UseQueryOptions<SpeedResultQuery, SpeedResultQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<SpeedResultQuery, SpeedResultQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<SpeedResultQuery, SpeedResultQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<SpeedResultQuery, SpeedResultQueryVariables>(SpeedResultDocument, variables, options);
}
export function useSpeedResultLazyQuery(variables?: SpeedResultQueryVariables | VueCompositionApi.Ref<SpeedResultQueryVariables> | ReactiveFunction<SpeedResultQueryVariables>, options: VueApolloComposable.UseQueryOptions<SpeedResultQuery, SpeedResultQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<SpeedResultQuery, SpeedResultQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<SpeedResultQuery, SpeedResultQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<SpeedResultQuery, SpeedResultQueryVariables>(SpeedResultDocument, variables, options);
}
export type SpeedResultQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<SpeedResultQuery, SpeedResultQueryVariables>;
export const SpeedResultsDocument = gql`
    query SpeedResults($limit: Int, $startAfter: Timestamp, $eventDefinitionId: ID, $groupId: ID) {
  me {
    id
    speedResults(
      limit: $limit
      startAfter: $startAfter
      eventDefinitionId: $eventDefinitionId
      groupId: $groupId
    ) {
      ...SpeedResultBase
    }
  }
}
    ${SpeedResultBaseFragmentDoc}`;

/**
 * __useSpeedResultsQuery__
 *
 * To run a query within a Vue component, call `useSpeedResultsQuery` and pass it any options that fit your needs.
 * When your component renders, `useSpeedResultsQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param variables that will be passed into the query
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useSpeedResultsQuery({
 *   limit: // value for 'limit'
 *   startAfter: // value for 'startAfter'
 *   eventDefinitionId: // value for 'eventDefinitionId'
 *   groupId: // value for 'groupId'
 * });
 */
export function useSpeedResultsQuery(variables: SpeedResultsQueryVariables | VueCompositionApi.Ref<SpeedResultsQueryVariables> | ReactiveFunction<SpeedResultsQueryVariables> = {}, options: VueApolloComposable.UseQueryOptions<SpeedResultsQuery, SpeedResultsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<SpeedResultsQuery, SpeedResultsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<SpeedResultsQuery, SpeedResultsQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<SpeedResultsQuery, SpeedResultsQueryVariables>(SpeedResultsDocument, variables, options);
}
export function useSpeedResultsLazyQuery(variables: SpeedResultsQueryVariables | VueCompositionApi.Ref<SpeedResultsQueryVariables> | ReactiveFunction<SpeedResultsQueryVariables> = {}, options: VueApolloComposable.UseQueryOptions<SpeedResultsQuery, SpeedResultsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<SpeedResultsQuery, SpeedResultsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<SpeedResultsQuery, SpeedResultsQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<SpeedResultsQuery, SpeedResultsQueryVariables>(SpeedResultsDocument, variables, options);
}
export type SpeedResultsQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<SpeedResultsQuery, SpeedResultsQueryVariables>;
export const TagsDocument = gql`
    query Tags($lang: String) {
  tags {
    id
    slug
    disciplines
    name(lang: $lang)
    valueType
    system
    values {
      id
      name(lang: $lang)
    }
  }
}
    `;

/**
 * __useTagsQuery__
 *
 * To run a query within a Vue component, call `useTagsQuery` and pass it any options that fit your needs.
 * When your component renders, `useTagsQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param variables that will be passed into the query
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useTagsQuery({
 *   lang: // value for 'lang'
 * });
 */
export function useTagsQuery(variables: TagsQueryVariables | VueCompositionApi.Ref<TagsQueryVariables> | ReactiveFunction<TagsQueryVariables> = {}, options: VueApolloComposable.UseQueryOptions<TagsQuery, TagsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<TagsQuery, TagsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<TagsQuery, TagsQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<TagsQuery, TagsQueryVariables>(TagsDocument, variables, options);
}
export function useTagsLazyQuery(variables: TagsQueryVariables | VueCompositionApi.Ref<TagsQueryVariables> | ReactiveFunction<TagsQueryVariables> = {}, options: VueApolloComposable.UseQueryOptions<TagsQuery, TagsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<TagsQuery, TagsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<TagsQuery, TagsQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<TagsQuery, TagsQueryVariables>(TagsDocument, variables, options);
}
export type TagsQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<TagsQuery, TagsQueryVariables>;
export const TrickBySlugDocument = gql`
    query TrickBySlug($discipline: Discipline!, $slug: String!, $withLocalised: Boolean!, $lang: String) {
  trick: trickBySlug(discipline: $discipline, slug: $slug) {
    ...TrickBox
    en: localisation {
      id
      name
      alternativeNames
      description
    }
    localised: localisation(lang: $lang) @include(if: $withLocalised) {
      id
      name
      alternativeNames
      description
    }
    videos {
      videoId
      type
      host
    }
    contributors {
      name
    }
    levels {
      id
      rulesId
      level
      verificationLevel
    }
    prerequisites {
      ...TrickBox
    }
    prerequisiteFor {
      ...TrickBox
    }
  }
}
    ${TrickBoxFragmentDoc}`;

/**
 * __useTrickBySlugQuery__
 *
 * To run a query within a Vue component, call `useTrickBySlugQuery` and pass it any options that fit your needs.
 * When your component renders, `useTrickBySlugQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param variables that will be passed into the query
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useTrickBySlugQuery({
 *   discipline: // value for 'discipline'
 *   slug: // value for 'slug'
 *   withLocalised: // value for 'withLocalised'
 *   lang: // value for 'lang'
 * });
 */
export function useTrickBySlugQuery(variables: TrickBySlugQueryVariables | VueCompositionApi.Ref<TrickBySlugQueryVariables> | ReactiveFunction<TrickBySlugQueryVariables>, options: VueApolloComposable.UseQueryOptions<TrickBySlugQuery, TrickBySlugQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<TrickBySlugQuery, TrickBySlugQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<TrickBySlugQuery, TrickBySlugQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<TrickBySlugQuery, TrickBySlugQueryVariables>(TrickBySlugDocument, variables, options);
}
export function useTrickBySlugLazyQuery(variables?: TrickBySlugQueryVariables | VueCompositionApi.Ref<TrickBySlugQueryVariables> | ReactiveFunction<TrickBySlugQueryVariables>, options: VueApolloComposable.UseQueryOptions<TrickBySlugQuery, TrickBySlugQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<TrickBySlugQuery, TrickBySlugQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<TrickBySlugQuery, TrickBySlugQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<TrickBySlugQuery, TrickBySlugQueryVariables>(TrickBySlugDocument, variables, options);
}
export type TrickBySlugQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<TrickBySlugQuery, TrickBySlugQueryVariables>;
export const MyTrickSubmissionsDocument = gql`
    query MyTrickSubmissions($withLocalised: Boolean!, $lang: String) {
  me {
    id
    trickSubmissions {
      ...TrickSubmissionBase
      trick {
        id
        en: localisation {
          id
          name
        }
        localised: localisation(lang: $lang) @include(if: $withLocalised) {
          id
          name
        }
      }
    }
  }
}
    ${TrickSubmissionBaseFragmentDoc}`;

/**
 * __useMyTrickSubmissionsQuery__
 *
 * To run a query within a Vue component, call `useMyTrickSubmissionsQuery` and pass it any options that fit your needs.
 * When your component renders, `useMyTrickSubmissionsQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param variables that will be passed into the query
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useMyTrickSubmissionsQuery({
 *   withLocalised: // value for 'withLocalised'
 *   lang: // value for 'lang'
 * });
 */
export function useMyTrickSubmissionsQuery(variables: MyTrickSubmissionsQueryVariables | VueCompositionApi.Ref<MyTrickSubmissionsQueryVariables> | ReactiveFunction<MyTrickSubmissionsQueryVariables>, options: VueApolloComposable.UseQueryOptions<MyTrickSubmissionsQuery, MyTrickSubmissionsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<MyTrickSubmissionsQuery, MyTrickSubmissionsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<MyTrickSubmissionsQuery, MyTrickSubmissionsQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<MyTrickSubmissionsQuery, MyTrickSubmissionsQueryVariables>(MyTrickSubmissionsDocument, variables, options);
}
export function useMyTrickSubmissionsLazyQuery(variables?: MyTrickSubmissionsQueryVariables | VueCompositionApi.Ref<MyTrickSubmissionsQueryVariables> | ReactiveFunction<MyTrickSubmissionsQueryVariables>, options: VueApolloComposable.UseQueryOptions<MyTrickSubmissionsQuery, MyTrickSubmissionsQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<MyTrickSubmissionsQuery, MyTrickSubmissionsQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<MyTrickSubmissionsQuery, MyTrickSubmissionsQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<MyTrickSubmissionsQuery, MyTrickSubmissionsQueryVariables>(MyTrickSubmissionsDocument, variables, options);
}
export type MyTrickSubmissionsQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<MyTrickSubmissionsQuery, MyTrickSubmissionsQueryVariables>;
export const TricksDocument = gql`
    query Tricks($discipline: Discipline, $searchQuery: String, $withLocalised: Boolean!, $lang: String) {
  tricks(discipline: $discipline, searchQuery: $searchQuery) {
    ...TrickBox
  }
}
    ${TrickBoxFragmentDoc}`;

/**
 * __useTricksQuery__
 *
 * To run a query within a Vue component, call `useTricksQuery` and pass it any options that fit your needs.
 * When your component renders, `useTricksQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param variables that will be passed into the query
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useTricksQuery({
 *   discipline: // value for 'discipline'
 *   searchQuery: // value for 'searchQuery'
 *   withLocalised: // value for 'withLocalised'
 *   lang: // value for 'lang'
 * });
 */
export function useTricksQuery(variables: TricksQueryVariables | VueCompositionApi.Ref<TricksQueryVariables> | ReactiveFunction<TricksQueryVariables>, options: VueApolloComposable.UseQueryOptions<TricksQuery, TricksQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<TricksQuery, TricksQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<TricksQuery, TricksQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<TricksQuery, TricksQueryVariables>(TricksDocument, variables, options);
}
export function useTricksLazyQuery(variables?: TricksQueryVariables | VueCompositionApi.Ref<TricksQueryVariables> | ReactiveFunction<TricksQueryVariables>, options: VueApolloComposable.UseQueryOptions<TricksQuery, TricksQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<TricksQuery, TricksQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<TricksQuery, TricksQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<TricksQuery, TricksQueryVariables>(TricksDocument, variables, options);
}
export type TricksQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<TricksQuery, TricksQueryVariables>;
export const UiMessagesDocument = gql`
    query UiMessages($lang: String!) {
  uiMessages(lang: $lang)
}
    `;

/**
 * __useUiMessagesQuery__
 *
 * To run a query within a Vue component, call `useUiMessagesQuery` and pass it any options that fit your needs.
 * When your component renders, `useUiMessagesQuery` returns an object from Apollo Client that contains result, loading and error properties
 * you can use to render your UI.
 *
 * @param variables that will be passed into the query
 * @param options that will be passed into the query, supported options are listed on: https://v4.apollo.vuejs.org/guide-composable/query.html#options;
 *
 * @example
 * const { result, loading, error } = useUiMessagesQuery({
 *   lang: // value for 'lang'
 * });
 */
export function useUiMessagesQuery(variables: UiMessagesQueryVariables | VueCompositionApi.Ref<UiMessagesQueryVariables> | ReactiveFunction<UiMessagesQueryVariables>, options: VueApolloComposable.UseQueryOptions<UiMessagesQuery, UiMessagesQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<UiMessagesQuery, UiMessagesQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<UiMessagesQuery, UiMessagesQueryVariables>> = {}) {
  return VueApolloComposable.useQuery<UiMessagesQuery, UiMessagesQueryVariables>(UiMessagesDocument, variables, options);
}
export function useUiMessagesLazyQuery(variables?: UiMessagesQueryVariables | VueCompositionApi.Ref<UiMessagesQueryVariables> | ReactiveFunction<UiMessagesQueryVariables>, options: VueApolloComposable.UseQueryOptions<UiMessagesQuery, UiMessagesQueryVariables> | VueCompositionApi.Ref<VueApolloComposable.UseQueryOptions<UiMessagesQuery, UiMessagesQueryVariables>> | ReactiveFunction<VueApolloComposable.UseQueryOptions<UiMessagesQuery, UiMessagesQueryVariables>> = {}) {
  return VueApolloComposable.useLazyQuery<UiMessagesQuery, UiMessagesQueryVariables>(UiMessagesDocument, variables, options);
}
export type UiMessagesQueryCompositionFunctionResult = VueApolloComposable.UseQueryReturn<UiMessagesQuery, UiMessagesQueryVariables>;